import useOperationalClock from '../core/shifts/useOperationalClock';
import useTenantTimeZone from '../components/common/useTenantTimeZone';
import { resolveShiftTimeZone, localDateTimeToInstant, zonedInputValue, regionalShiftInterval, zonedParts, formatShiftInstant } from '../core/shifts/shiftTimeZone';
import PlannerHistory from "../components/planner/PlannerHistory";
import PlannerDailyBoard from "../components/planner/PlannerDailyBoard";
import PlannerDialog from "../components/planner/PlannerDialog";
import PlannerCreateShiftPanel from "../components/planner/PlannerCreateShiftPanel";
import PlannerNavigation, { PlannerWorkspaceHeader } from "../components/planner/PlannerNavigation";
import { buildDailyBoard, selectDailyShifts } from "../core/shifts/shiftDailyBoard";
import "../styles/plannerDailyBoard.css";
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { signOut } from "firebase/auth";
import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from "firebase/firestore";
import PlannerShiftCard from "../components/planner/PlannerShiftCard";
import DesktopBoard from "../components/planner/DesktopBoard";
import MobileBoard from "../components/planner/MobileBoard";
import OverflowPanelModal from "../components/planner/OverflowPanelModal";

import { auth, db } from "../firebase";
import "../styles/planner.css";
import LifecycleBoxes from "../components/LifecycleBoxes";
import SourceModeTabs from "../components/SourceModeTabs";
import {
  roleColorFor,
} from "../core/shifts/shiftPresentation";
import {
  getShiftOperationalSummary,
} from "../core/shifts/shiftOperationalSummary";

import {
  createShift,
  reassignWorkerOnShift,
  approvePoolRequest,
  declinePoolRequest,
  updateShiftTime,
  confirmShiftTime,
  cancelShift as removeShift,
  closeShiftUnfilled,

  completeShift as setShiftCompleted,
  markShiftNoShow as setShiftNoShow,
} from "../core/shifts/shiftService";
import {
  getShiftStartMs,
  getShiftEndMs,
} from "../core/shifts/shiftLifecycle";
import { buildShiftPayload } from "../utils";
import { SHIFT_STATUS } from "../constants";
import { ShiftBridgeAssist } from "../components/ShiftBridgeAssist";
import getWorkerCompliance from "../utils/compliance/getWorkerCompliance";
import { useDateScope } from "../context/DateScopeContext";
import { calculateReliabilityScore } from "../modules/workforce";
import DepartmentLayout from "../enterprise/layouts/DepartmentLayout";
import EnterpriseToolbar from "../enterprise/components/EnterpriseToolbar";
import PlannerHeader from "../components/planner/PlannerHeader";
import PlannerAlerts, { AutoNoShowControl } from "../components/planner/PlannerAlerts";
import ScheduleWorkspace from "../components/planner/ScheduleWorkspace";
import "../styles/plannerSchedule.css";
import WorkerCard from "../components/planner/WorkerCard";
import StaffPoolPanel from "../components/planner/StaffPoolPanel";
import { PlannerProvider } from "../components/planner/PlannerContext";
import usePlannerSelection from "../hooks/usePlannerSelection";
import usePlannerFilters from "../hooks/usePlannerFilters";
import usePlannerPanels from "../hooks/usePlannerPanels";
import usePlannerMetrics from "../hooks/usePlannerMetrics";
import FacilityRatingModal from "../components/FacilityRatingModal";
import { saveFacilityRating } from "../services/ratings/saveFacilityRating";
import { buildSmartMatch } from "../services/talent/SmartMatchEngine";
import SelectedShiftAssistant, { passesRecommendationHardGuards } from "../components/SelectedShiftAssistant";
import Shift360Overview from "../modules/shifts/components/Shift360Overview";
import Shift360Timeline from "../modules/shifts/components/Shift360Timeline";
import Shift360Audit from "../modules/shifts/components/Shift360Audit";
import Shift360Location from "../modules/shifts/components/Shift360Location";
import Shift360AIInsights from "../modules/shifts/components/Shift360AIInsights";
import Shift360Incidents from "../modules/shifts/components/Shift360Incidents";
import Shift360Communications from "../modules/shifts/components/Shift360Communications";
import {
  getWorkerRoles,
  getWorkerPrimaryRole,
  workerCanFillRole,
} from "../modules/workforce/utils/workerRoles";
import useOperationalScope from "../core/scope/useOperationalScope";
import useFacilityShifts from "../core/data/useFacilityShifts";
import useFacilityProfile from "../core/data/useFacilityProfile";
import useFacilityWorkers from "../core/data/useFacilityWorkers";
import { getOperationalWorkers } from "../core/workforce/workerEligibility";
import {
  getShiftEligibilityResults,
  summarizeShiftEligibility,
} from "../core/workforce/shiftEligibility";

const DEFAULT_ROLES = ["CNA", "LVN", "RN", "MED_AIDE", "CARE_GIVER"];
const DISPLAY_ROLE_ORDER = ["CNA", "LVN", "RN", "MED_AIDE", "CARE_GIVER"];

const BOARD_BLOCKS = [
  { key: "06_14", label: "6 AM → 2 PM", startHour: 6, endHour: 14 },
  { key: "14_22", label: "2 PM → 10 PM", startHour: 14, endHour: 22 },
  { key: "22_06", label: "10 PM → 6 AM", startHour: 22, endHour: 6 },
  { key: "18_06", label: "6 PM → 6 AM", startHour: 18, endHour: 6 },
  { key: "19_07", label: "7 PM → 7 AM", startHour: 19, endHour: 7 },
];

const SHIFT_PRESETS = [
  {
    key: "06_14",
    label: "6 AM → 2 PM",
    startHour: 6,
    endHour: 14,
  },
  {
    key: "14_22",
    label: "2 PM → 10 PM",
    startHour: 14,
    endHour: 22,
  },
  {
    key: "22_06",
    label: "10 PM → 6 AM",
    startHour: 22,
    endHour: 6,
    overnight: true,
  },
  {
    key: "07_19",
    label: "7 AM → 7 PM",
    startHour: 7,
    endHour: 19,
  },
  {
    key: "19_07",
    label: "7 PM → 7 AM",
    startHour: 19,
    endHour: 7,
    overnight: true,
  },
  {
    key: "06_18",
    label: "6 AM → 6 PM",
    startHour: 6,
    endHour: 18,
  },
  {
    key: "18_06",
    label: "6 PM → 6 AM",
    startHour: 18,
    endHour: 6,
    overnight: true,
  },
  {
    key: "flex",
    label: "Flexible Emergency Fill",
    isFlexibleTime: true,
  },
];


function toMillis(v) {
  if (!v) return NaN;
  try {
    if (typeof v?.toDate === "function") return v.toDate().getTime();
    if (typeof v === "number") return v;
    return new Date(v).getTime();
  } catch {
    return NaN;
  }
}

function getBoardMetrics(shifts = []) {
  const terminalStatuses = new Set([
    "completed",
    "cancelled",
    "canceled",
    "no_show",
    "closed_unfilled",
  ]);

  const live = shifts.filter((shift) => {
    const status = String(
      shift?.status || "open"
    )
      .trim()
      .toLowerCase();

    return !terminalStatuses.has(status);
  });

  const uncovered = live.filter((shift) => {
    const status = String(
      shift?.status || "open"
    )
      .trim()
      .toLowerCase();

    const assigned =
      shift?.assignedWorkerUid ||
      shift?.workerUid ||
      shift?.workerId ||
      (
        Array.isArray(
          shift?.assignedWorkerUids
        ) &&
        shift.assignedWorkerUids.length > 0
      );

    return (
      !assigned &&
      [
        "open",
        "offered",
        "requested",
        "time_required",
      ].includes(status)
    );
  });

  const assigned = live.filter((shift) => {
    const status = String(
      shift?.status || ""
    )
      .trim()
      .toLowerCase();

    return [
      "assigned",
      "activated",
      "tracking",
      "arrived",
    ].includes(status);
  });

  const statusCounts = shifts.reduce(
    (acc, shift) => {
      const status = String(
        shift?.status || "open"
      )
        .trim()
        .toLowerCase();

      acc[status] =
        (acc[status] || 0) + 1;

      return acc;
    },
    {}
  );

  const uncoveredByRole =
    uncovered.reduce(
      (acc, shift) => {
        const rk =
          roleKey(
            shift?.role
          );

        acc[rk] =
          (acc[rk] || 0) + 1;

        return acc;
      },
      {}
    );

  return {
    live,
    uncovered,
    assigned,
    uncoveredByRole,
    statusCounts,
    totalUncovered:
      uncovered.length,
    totalAssigned:
      assigned.length,
  };
}

function roleKey(v) {
  return String(v || "")
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, "_")
    .replace(/_+/g, "_");
}

function availabilityStatusKey(v) {
  return String(v || "available").trim().toLowerCase();
}

function isWorkerAvailabilityBlocked(worker) {
  const status = availabilityStatusKey(worker?.availabilityStatus);
  return ["off_today", "vacation", "sick"].includes(status);
}

function availabilityStatusMeta(worker) {
  const status = availabilityStatusKey(worker?.availabilityStatus);

  if (status === "off_today") {
    return {
      label: "OFF TODAY",
      bg: "#fff7ed",
      border: "#fdba74",
      color: "#9a3412",
    };
  }

  if (status === "vacation") {
    return {
      label: "VACATION",
      bg: "#fef3c7",
      border: "#facc15",
      color: "#92400e",
    };
  }

  if (status === "sick") {
    return {
      label: "SICK",
      bg: "#fee2e2",
      border: "#fca5a5",
      color: "#991b1b",
    };
  }

  return {
    label: "AVAILABLE",
    bg: "#dcfce7",
    border: "#86efac",
    color: "#166534",
  };
}

function startOfDayMs(ms) {
  const d = new Date(ms);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

function ymd(ms) {
  const d = new Date(ms);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function addDays(ms, n) {
  const calendar = new Date(ms);
  calendar.setDate(calendar.getDate() + n);
  return +calendar;
}

function toLocalInputValue(dateObj) {
  const pad = (n) => String(n).padStart(2, "0");
  const yyyy = dateObj.getFullYear();
  const mm = pad(dateObj.getMonth() + 1);
  const dd = pad(dateObj.getDate());
  const hh = pad(dateObj.getHours());
  const mi = pad(dateObj.getMinutes());
  return `${yyyy}-${mm}-${dd}T${hh}:${mi}`;
}

function fmtShortRange(startV, endV, shift = null) {
  if (shift?.isFlexibleTime) return "Flexible";

  const s = toMillis(startV);
  const e = toMillis(endV);
  if (!Number.isFinite(s) || !Number.isFinite(e)) return "—";

  const start = new Date(s);
  const end = new Date(e);
  const timeZone = shift?.timeZone || shift?.timezone;
  if (!timeZone) return "Timezone unavailable";

  return `${start.toLocaleTimeString([], {timeZone,
    hour: "numeric",
    minute: "2-digit",
  })} → ${end.toLocaleTimeString([], {timeZone,
    hour: "numeric",
    minute: "2-digit",
  })}`;
}

function getAssignedWorkerUid(shift) {
  if (!shift) return "";
  if (shift.assignedWorkerUid) return shift.assignedWorkerUid;
  if (Array.isArray(shift.assignedWorkerUids) && shift.assignedWorkerUids.length > 0) {
    return shift.assignedWorkerUids[0];
  }
  return "";
}

function getShiftMode(shift) {
  return String(shift?.mode || shift?.assignMode || "pool").toLowerCase();
}

function isShiftArrived(shift) {
  return !!shift?.arrivedAt || String(shift?.status || "").toLowerCase() === "arrived";
}

function isBlockingShiftStatus(status) {
  return [
    "assigned",
    "activated",
    "tracking",
    "arrived",
  ].includes(
    String(status || "")
      .trim()
      .toLowerCase()
  );
}

function rangesOverlap(aStart, aEnd, bStart, bEnd) {
  const a1 = toMillis(aStart);
  const a2 = toMillis(aEnd);
  const b1 = toMillis(bStart);
  const b2 = toMillis(bEnd);

  if (
    !Number.isFinite(a1) ||
    !Number.isFinite(a2) ||
    !Number.isFinite(b1) ||
    !Number.isFinite(b2)
  ) {
    return false;
  }

  return a1 < b2 && b1 < a2;
}

function hasShiftEnded(shift, nowMs = Date.now()) {
  const endMs = toMillis(shift?.endTime);
  return Number.isFinite(endMs) && nowMs >= endMs;
}

function shiftConflicts({
  start,
  end,
  role,
  facilityId,
  shifts,
  ignoreShiftId = null,
}) {
  if (
    !(start instanceof Date) ||
    !(end instanceof Date)
  ) {
    return false;
  }

  if (
    !Number.isFinite(start.getTime()) ||
    !Number.isFinite(end.getTime())
  ) {
    return false;
  }

  const terminalStatuses = new Set([
    "completed",
    "cancelled",
    "canceled",
    "no_show",
    "closed_unfilled",
  ]);

  return (shifts || []).some((shift) => {
    if (
      ignoreShiftId &&
      shift?.id === ignoreShiftId
    ) {
      return false;
    }

    const status = String(
      shift?.status || ""
    )
      .trim()
      .toLowerCase();

    /*
     * Terminal shifts do not block
     * creation or time editing.
     */
    if (
      terminalStatuses.has(status)
    ) {
      return false;
    }

    /*
     * Flexible shifts without fixed time
     * do not participate in time conflicts.
     */
    if (shift?.isFlexibleTime) {
      return false;
    }

    const shiftFacilityId = String(
      shift?.facilityId || ""
    )
      .trim()
      .toLowerCase();

    const targetFacilityId = String(
      facilityId || ""
    )
      .trim()
      .toLowerCase();

    if (
      shiftFacilityId !==
      targetFacilityId
    ) {
      return false;
    }

    /*
     * This function checks duplicate/overlapping
     * demand for the same role.
     */
    if (
      roleKey(shift?.role) !==
      roleKey(role)
    ) {
      return false;
    }

    return rangesOverlap(
      start.getTime(),
      end.getTime(),
      shift?.startTime,
      shift?.endTime
    );
  });
}

function workerHasConflict(workerUid, start, end, shifts, ignoreShiftId = null) {
  const startMs = start instanceof Date ? start.getTime() : Number(start);
  const endMs = end instanceof Date ? end.getTime() : Number(end);

  if (!Number.isFinite(startMs) || !Number.isFinite(endMs)) return false;

  return (shifts || []).some((s) => {
    if (ignoreShiftId && s.id === ignoreShiftId) return false;
    if (String(s.status || "").toLowerCase() === "cancelled") return false;
    if (s?.isFlexibleTime) return false;

    const assignedUid = getAssignedWorkerUid(s);
    if (!assignedUid || assignedUid !== workerUid) return false;
    if (!isBlockingShiftStatus(s.status)) return false;

    return rangesOverlap(startMs, endMs, s.startTime, s.endTime);
  });
}

function normalizeWorkerCredSummary(worker) {
  const creds = Array.isArray(worker?.credentials) ? worker.credentials : [];
  const out = new Map();

  for (const c of creds) {
    if (typeof c === "string") {
      const t = roleKey(c);
      if (!t) continue;
      out.set(t, { type: t, valid: true });
      continue;
    }

    if (c && typeof c === "object") {
      const t = roleKey(c.type || c.credential || "");
      if (!t) continue;
      const verified = c.verified === true;
      const expiresAt = toMillis(c.expiresAt);
      const expired = Number.isFinite(expiresAt) ? expiresAt < Date.now() : false;
      out.set(t, { type: t, valid: verified && !expired });
    }
  }

  const all = Array.from(out.values()).sort((a, b) => a.type.localeCompare(b.type));
  const validTypes = all.filter((x) => x.valid).map((x) => x.type);

  return {
    allCreds: all,
    validTypes,
    hasValidCred: validTypes.length > 0,
  };
}

function cardStyle() {
  return {
    background: "#fff",
    border: "1px solid #d8e1ea",
    borderRadius: 16,
    boxShadow: "0 8px 22px rgba(15,23,42,.05)",
  };
}
function nameFromEmail(email) {
  const local = String(email || "").split("@")[0] || "";
  if (!local) return "";

  const parts = local.split(/[._-]+/).filter(Boolean);
  const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : "");

  if (parts.length >= 2) {
    return `${cap(parts[0])} ${cap(parts[1]).charAt(0)}.`;
  }

  return local.length > 12 ? `${local.slice(0, 12)}…` : local;
}

function shortWorkerLabel(workersByUidMap, uid) {
  if (!uid) return "";
  const w = workersByUidMap.get(uid);
  return nameFromEmail(w?.email) || uid.slice(0, 6);
}

function cellHeightForBlock() {
  return 168;
}

function cardHeightForCount(count) {
  if (count <= 1) return 96;
  if (count === 2) return 72;
  if (count === 3) return 52;
  if (count === 4) return 38;
  return 28;
}

function visibleCardsForCount(count) {
  if (count <= 1) return 1;
  if (count === 2) return 2;
  if (count === 3) return 3;
  if (count === 4) return 4;
  return 5;
}

function plannerBlockTitle(label) {
  const raw = String(label || "");
  const normalized = raw.replace(/\s+/g, "");

  if (normalized.includes("06") && normalized.includes("14")) {
    return { icon: "🌅", title: "Morning", time: "06:00–14:00" };
  }

  if (normalized.includes("14") && normalized.includes("22")) {
    return { icon: "🌇", title: "Afternoon", time: "14:00–22:00" };
  }

  if (normalized.includes("22") && normalized.includes("06")) {
    return { icon: "🌙", title: "Overnight", time: "22:00–06:00" };
  }

  if (normalized.includes("07") && normalized.includes("19")) {
    return { icon: "☀", title: "Day Shift", time: "07:00–19:00" };
  }

  if (normalized.includes("06") && normalized.includes("18")) {
    return { icon: "🏥", title: "12-Hour", time: "06:00–18:00" };
  }

  return { icon: "🕒", title: raw || "Shift Block", time: "" };
}


function getShiftMs(value) {
  if (!value) return NaN;

  if (typeof value?.toDate === "function") {
    return value.toDate().getTime();
  }

  if (value?.seconds) {
    return value.seconds * 1000;
  }

  const d = new Date(value);
  return d.getTime();
}

function isExpiredShift(shift) {
  const end = getShiftMs(
    shift?.endTime ||
    shift?.end ||
    shift?.shiftEnd ||
    shift?.endDateTime
  );

  if (!Number.isFinite(end)) return false;

  return end <= Date.now();
}

function isActiveCompletedShift(
  shift
) {
  const operational =
    getShiftOperationalSummary(
      shift
    );

  return (
    operational.status ===
    "completed"
  );
}

export default function FacilityPlanner({
  profile,
  operationalContext = {},
  embedded = false,
}) {
  const [plannerView, setPlannerView] = useState("daily");
  const [createPanelOpen, setCreatePanelOpen] = useState(false);
  const [workspacePanel, setWorkspacePanel] = useState(null);
  const [scheduleFocus, setScheduleFocus] = useState(null);
  useEffect(() => { setScheduleFocus(null); }, [operationalContext.facilityId, profile?.facilityId]);
  const plannerScope = useOperationalScope(
    profile,
    operationalContext
  );

  console.log("FIREBASE PROJECT AUDIT", {
    authProjectId:
      auth?.app?.options?.projectId || "",
    firestoreProjectId:
      db?.app?.options?.projectId || "",
    currentUid:
      auth?.currentUser?.uid || "",
    currentEmail:
      auth?.currentUser?.email || "",
  });

  const {
    uid: plannerUid,
    email: plannerEmail,
    role: plannerRole,

    tenantId,
    facilityId,
    primaryFacilityId,
    allowedFacilityIds,

    isPlanner,
    isPlannerWorkspace,
    canOperatePlannerWorkspace,
    hasTenantScope,
    hasFacilityScope,
    isOperationallyScoped,
  } = plannerScope;

  useEffect(() => {
    console.log("PLANNER OPERATIONAL SCOPE", {
      plannerUid,
      plannerEmail,
      plannerRole,
      tenantId,
      facilityId,
      primaryFacilityId,
      allowedFacilityIds,
      isPlanner,
      isPlannerWorkspace,
      canOperatePlannerWorkspace,
      hasTenantScope,
      hasFacilityScope,
      isOperationallyScoped,
    });
  }, [
    plannerUid,
    plannerEmail,
    plannerRole,
    tenantId,
    facilityId,
    primaryFacilityId,
    allowedFacilityIds,
    isPlanner,
    isPlannerWorkspace,
    canOperatePlannerWorkspace,
    hasTenantScope,
    hasFacilityScope,
    isOperationallyScoped,
  ]);

  useEffect(() => {
    const rawTenantId = profile?.tenantId;

    console.log("PLANNER RAW TENANT AUDIT", {
      value: rawTenantId,
      type: typeof rawTenantId,

      length:
        typeof rawTenantId === "string"
          ? rawTenantId.length
          : null,

      json:
        typeof rawTenantId === "string"
          ? JSON.stringify(rawTenantId)
          : null,

      characterCodes:
        typeof rawTenantId === "string"
          ? Array.from(rawTenantId).map(
              (char) => char.charCodeAt(0)
            )
          : [],
    });
  }, [profile]);

  console.log("PLANNER SCOPE AUDIT", {
    uid: profile?.uid,
    email: profile?.email,
    role: profile?.role,
    platformRole: profile?.platformRole,

    tenantId,

    profileFacilityId: profile?.facilityId,
    profilePrimaryFacilityId:
      profile?.primaryFacilityId,

    profileFacilityIds:
      profile?.facilityIds,

    profileAllowedFacilityIds:
      profile?.allowedFacilityIds,

    primaryFacilityId,
    allowedFacilityIds,
    facilityId,
  });

  useEffect(() => {
    let mounted = true;

    async function loadAccessPermissions() {
      const uid =
        auth.currentUser?.uid ||
        profile?.uid ||
        profile?.id ||
        "";

      if (!uid) {
        if (mounted) {
          setAccessPermissions({});
        }

        return;
      }

      try {
        const accessSnapshot = await getDoc(
          doc(db, "accessProfiles", uid)
        );

        const accessData = accessSnapshot.exists()
          ? accessSnapshot.data() || {}
          : {};

        console.log(
          "PLANNER ACCESS PROFILE AUDIT",
          {
            uid,
            exists: accessSnapshot.exists(),
            active: accessData?.active,
            role: accessData?.role,
            facilityId: accessData?.facilityId,
            facilityIds:
              accessData?.facilityIds,
            allowedFacilityIds:
              accessData?.allowedFacilityIds,
            canViewIncidents:
              accessData?.permissions
                ?.canViewIncidents,
            canViewCommunications:
              accessData?.permissions
                ?.canViewCommunications,
          }
        );

        if (mounted) {
          setAccessPermissions(
            accessData.permissions &&
              typeof accessData.permissions ===
                "object"
              ? accessData.permissions
              : {}
          );
        }
      } catch (error) {
        console.error(
          "PLANNER PERMISSIONS LOAD ERROR:",
          error
        );

        if (mounted) {
          setAccessPermissions({});
        }
      }
    }

    loadAccessPermissions();

    return () => {
      mounted = false;
    };
  }, [profile?.uid, profile?.id]);

  const [mode, setMode] = useState("manual");
  const [allowedRoles, setAllowedRoles] =
    useState([]);
  const [incidents, setIncidents] =
    useState([]);
  const [
    communications,
    setCommunications,
  ] = useState([]);
  const [
    accessPermissions,
    setAccessPermissions,
  ] = useState({});

  const normalizedProfileRole = String(
    profile?.platformRole ||
      profile?.role ||
      ""
  )
    .trim()
    .toLowerCase();

  const isFacilityPlannerProfile = [
    "planner",
    "facility_planner",
    "facilityplanner",
    "facility",
  ].includes(normalizedProfileRole);

  const canViewShiftFinancial =
    !isFacilityPlannerProfile ||
    accessPermissions.canViewBillRates ===
      true ||
    accessPermissions.canViewPayroll ===
      true ||
    accessPermissions.canViewInvoices ===
      true;

  const canViewShiftIncidents =
    !isFacilityPlannerProfile ||
    accessPermissions.canViewIncidents ===
      true;

  const canViewShiftCommunications =
    !isFacilityPlannerProfile ||
    accessPermissions
      .canViewCommunications === true;

  const [error, setError] = useState("");

  const {
    shifts: rawShifts,
    loading: shiftsLoading,
    error: shiftsDataError,
  } = useFacilityShifts({
    tenantId,
    facilityId,
    allowedFacilityIds,

    enabled:
      isPlannerWorkspace &&
      canOperatePlannerWorkspace &&
      hasTenantScope &&
      hasFacilityScope,

    debugLabel: "PLANNER SHIFTS",
  });

  const {
    workers: facilityWorkers,
    loading: facilityWorkersLoading,
    error: facilityWorkersDataError,
  } = useFacilityWorkers({
    tenantId,
    facilityId,
    allowedFacilityIds,

    enabled:
      isPlannerWorkspace &&
      canOperatePlannerWorkspace &&
      hasTenantScope &&
      hasFacilityScope,

    debugLabel:
      "PLANNER FACILITY WORKERS",
  });

  const workers = useMemo(
    () =>
      getOperationalWorkers({
        workers: facilityWorkers,
        getCompliance:
          getWorkerCompliance,
      }),
    [facilityWorkers]
  );

  useEffect(() => {
    console.log(
      "PLANNER WORKER PIPELINE",
      {
        facilityWorkers:
          facilityWorkers?.length || 0,

        operationalWorkers:
          workers?.length || 0,

        tenantId,
        facilityId,
      }
    );
  }, [
    facilityWorkers,
    workers,
    tenantId,
    facilityId,
  ]);

  useEffect(() => {
    if (!facilityWorkersDataError) return;

    console.error(
      "PLANNER FACILITY WORKERS DATA ERROR",
      {
        tenantId,
        facilityId,
        code:
          facilityWorkersDataError?.code,
        message:
          facilityWorkersDataError?.message,
      }
    );
  }, [
    facilityWorkersDataError,
    tenantId,
    facilityId,
  ]);

  const {
    facilityProfile:
      facilityProfileData,
    error: facilityProfileDataError,
  } = useFacilityProfile({
    tenantId,
    facilityId,

    enabled:
      isPlannerWorkspace &&
      canOperatePlannerWorkspace &&
      hasTenantScope &&
      hasFacilityScope,

    debugLabel:
      "PLANNER FACILITY PROFILE",
  });

  /*
   * Keep FacilityPlanner compatible with existing
   * code that expects facilityProfile to always
   * be an object.
   */
  const facilityProfile =
    facilityProfileData || {};
  const tenantTimeZone = useTenantTimeZone(tenantId);
  const facilityTimeZone = resolveShiftTimeZone(null, facilityProfile, {timeZone: tenantTimeZone});
  const shifts = useMemo(() => rawShifts.map(shift => ({...shift, timeZone: resolveShiftTimeZone(shift, facilityProfile, {timeZone: tenantTimeZone})})), [rawShifts, facilityProfile, tenantTimeZone]);

  useEffect(() => {
    const roles =
      Array.isArray(
        facilityProfile?.allowedRoles
      ) &&
      facilityProfile.allowedRoles.length >
        0
        ? facilityProfile.allowedRoles
        : DEFAULT_ROLES;

    setAllowedRoles(roles);

    setNewShiftRole(
      (current) =>
        current ||
        roles[0] ||
        ""
    );
  }, [facilityProfile]);

  useEffect(() => {
    if (!facilityProfileDataError) return;

    console.error(
      "PLANNER FACILITY PROFILE DATA ERROR",
      {
        tenantId,
        facilityId,
        code:
          facilityProfileDataError?.code,
        message:
          facilityProfileDataError
            ?.message,
      }
    );
  }, [
    facilityProfileDataError,
    tenantId,
    facilityId,
  ]);

  useEffect(() => {
    if (!shiftsDataError) return;

    setError(
      shiftsDataError?.message ||
        "Failed to subscribe to shifts."
    );
  }, [shiftsDataError]);

  const now = useOperationalClock();

  const [
    viewportWidth,
    setViewportWidth,
  ] = useState(
    typeof window !== "undefined"
      ? window.innerWidth
      : 1280
  );

  const [
    selectedDayMs,
    setSelectedDayMs,
  ] = useState(
    startOfDayMs(Date.now())
  );

  const [
    calendarAnchorMs,
    setCalendarAnchorMs,
  ] = useState(
    startOfDayMs(Date.now())
  );

  const {
    dateScope,
    setDateScope,
  } = useDateScope();

  useEffect(() => {
    if (!facilityTimeZone) return;
    const today = new Date(`${zonedInputValue(Date.now(), facilityTimeZone).slice(0,10)}T12:00`);
    if (dateScope === 'tomorrow') today.setDate(today.getDate()+1);
    if (dateScope === 'today' || dateScope === 'tomorrow') {
      setSelectedDayMs(+today); setCalendarAnchorMs(+today);
    }
  }, [dateScope, facilityTimeZone]);

  const [
    newShiftRole,
    setNewShiftRole,
  ] = useState("");

  const [
    newShiftMode,
    setNewShiftMode,
  ] = useState("pool");

  const [
    newShiftPreset,
    setNewShiftPreset,
  ] = useState("06_14");

  const [
    newShiftStart,
    setNewShiftStart,
  ] = useState("");

  const [
    newShiftEnd,
    setNewShiftEnd,
  ] = useState("");

  const [
    selectedWorkerUid,
    setSelectedWorkerUid,
  ] = useState("");

  const [
    selectedDirectWorkerUids,
    setSelectedDirectWorkerUids,
  ] = useState([]);

  const [
    createQty,
    setCreateQty,
  ] = useState(1);

  const [
    showShift360More,
    setShowShift360More,
  ] = useState(false);

  const [
    shift360Section,
    setShift360Section,
  ] = useState("operations");

  useEffect(() => {
    const sectionIsAllowed =
      shift360Section !== "financial" ||
      canViewShiftFinancial;

    const incidentsAreAllowed =
      shift360Section !== "incidents" ||
      canViewShiftIncidents;

    const communicationsAreAllowed =
      shift360Section !==
        "communications" ||
      canViewShiftCommunications;

    if (
      !sectionIsAllowed ||
      !incidentsAreAllowed ||
      !communicationsAreAllowed
    ) {
      setShift360Section("overview");
    }
  }, [
    shift360Section,
    canViewShiftFinancial,
    canViewShiftIncidents,
    canViewShiftCommunications,
  ]);

  const {
    selectedShiftId,
    setSelectedShiftId,
    selectedShiftIds,
    setSelectedShiftIds,
    toggleShiftSelection,
    clearShiftSelection,
    clearSelectedShift,
  } = usePlannerSelection();

  const {
    overflowPanel,
    setOverflowPanel,
    overflowTitle,
    setOverflowTitle,
    completedPanelOpen,
    setCompletedPanelOpen,
    pastPanelOpen,
    setPastPanelOpen,
    calendarExpanded,
    setCalendarExpanded,
    expandedWorkerUid,
    setExpandedWorkerUid,
  } = usePlannerPanels();

  const [
    editStartTime,
    setEditStartTime,
  ] = useState("");

  const [
    editEndTime,
    setEditEndTime,
  ] = useState("");

  const [
    editWorkerUid,
    setEditWorkerUid,
  ] = useState("");

  const [
    setTimePreset,
    setSetTimePreset,
  ] = useState("06_14");

  const [
    showClosed,
    setShowClosed,
  ] = useState(false);

  const [
    ratingShift,
    setRatingShift,
  ] = useState(null);

  const [
    ratingWorker,
    setRatingWorker,
  ] = useState(null);

  const [
    recommendedWorker,
    setRecommendedWorker,
  ] = useState(null);

  const [
    poolFilter,
    setPoolFilter,
  ] = useState("all");

  const [
    showDisabledWorkers,
    setShowDisabledWorkers,
  ] = useState(false);

  const [
    matchSelectedShiftRole,
    setMatchSelectedShiftRole,
  ] = useState(false);

  const [
    hiddenRoleKeys,
    setHiddenRoleKeys,
  ] = useState([]);

  const {
    searchTerm,
    setSearchTerm,
    staffPoolTab,
    setStaffPoolTab,
    lifecycleFilter,
    setLifecycleFilter,
    mobileBoardRole,
    setMobileBoardRole,
  } = usePlannerFilters();

  const isMobile =
    viewportWidth < 700;

  const isTablet =
    viewportWidth >= 700 &&
    viewportWidth < 1040;

  const isNarrow =
    viewportWidth < 1040;

  // TEMPORARY FLICKER TEST
  useEffect(() => {
    return undefined;
  }, []);

  useEffect(() => {
    function onKeyDown(e) {
      if (e.key === "Escape") {
        setOverflowPanel(null);
        setOverflowTitle("");
      }
    }

    window.addEventListener(
      "keydown",
      onKeyDown
    );

    return () =>
      window.removeEventListener(
        "keydown",
        onKeyDown
      );
  }, []);

  useEffect(() => {
    const onResize = () =>
      setViewportWidth(
        window.innerWidth
      );

    if (
      typeof window !== "undefined"
    ) {
      window.addEventListener(
        "resize",
        onResize
      );

      return () =>
        window.removeEventListener(
          "resize",
          onResize
        );
    }
  }, []);

  useEffect(() => {
    const saved =
      typeof window !== "undefined"
        ? window.localStorage.getItem(
            "planner_hidden_roles"
          )
        : null;

    if (saved) {
      try {
        const parsed =
          JSON.parse(saved);

        if (Array.isArray(parsed)) {
          setHiddenRoleKeys(parsed);
        }
      } catch {
        // ignore
      }
    }
  }, []);

  useEffect(() => {
    if (
      typeof window !== "undefined"
    ) {
      window.localStorage.setItem(
        "planner_hidden_roles",
        JSON.stringify(
          hiddenRoleKeys
        )
      );
    }
  }, [hiddenRoleKeys]);

  useEffect(() => {
    if (!tenantId) {
      setIncidents([]);

      console.log(
        "INCIDENTS LISTENER NOT STARTED — tenantId missing",
        {
          email: profile?.email,
          facilityId,
        }
      );

      return undefined;
    }

    if (!facilityId) {
      setIncidents([]);

      console.log(
        "INCIDENTS LISTENER NOT STARTED — facilityId missing",
        {
          email: profile?.email,
          tenantId,
        }
      );

      return undefined;
    }

    if (!canViewShiftIncidents) {
      setIncidents([]);

      console.log(
        "INCIDENTS LISTENER NOT STARTED — permission denied by profile",
        {
          email: profile?.email,
          uid: profile?.uid,
          tenantId,
          facilityId,
          canViewShiftIncidents,
          accessPermission:
            accessPermissions
              ?.canViewIncidents,
        }
      );

      return undefined;
    }

    if (
      allowedFacilityIds.length > 0 &&
      !allowedFacilityIds.includes(
        facilityId
      )
    ) {
      setIncidents([]);

      console.error(
        "INCIDENTS LISTENER BLOCKED — facility outside allowed scope",
        {
          email: profile?.email,
          tenantId,
          facilityId,
          allowedFacilityIds,
        }
      );

      return undefined;
    }

    console.log(
      "INCIDENTS LISTENER START",
      {
        email: profile?.email,
        uid: profile?.uid,
        tenantId,
        facilityId,
        canViewShiftIncidents,
        accessPermission:
          accessPermissions
            ?.canViewIncidents,
      }
    );

    const incidentsQuery = query(
      collection(db, "incidents"),

      where(
        "tenantId",
        "==",
        tenantId
      ),

      where(
        "facilityId",
        "==",
        facilityId
      )
    );

    const unsubscribeIncidents =
      onSnapshot(
        incidentsQuery,
        (snap) => {
          const rows = snap.docs
            .map(
              (incidentDoc) => ({
                id: incidentDoc.id,
                ...incidentDoc.data(),
              })
            )
            .sort((a, b) => {
              const aTime = toMillis(
                a.incidentDate ||
                  a.occurredAt ||
                  a.reportedAt ||
                  a.createdAt
              );

              const bTime = toMillis(
                b.incidentDate ||
                  b.occurredAt ||
                  b.reportedAt ||
                  b.createdAt
              );

              return (
                (Number.isFinite(bTime)
                  ? bTime
                  : 0) -
                (Number.isFinite(aTime)
                  ? aTime
                  : 0)
              );
            });

          console.log(
            "INCIDENTS LISTENER SUCCESS",
            {
              tenantId,
              facilityId,
              size: rows.length,
            }
          );

          setIncidents(rows);
        },
        (err) => {
          console.error(
            "SHIFT 360 INCIDENTS LISTENER ERROR",
            {
              email:
                profile?.email,
              uid: profile?.uid,
              tenantId,
              facilityId,
              allowedFacilityIds,
              canViewShiftIncidents,
              accessPermission:
                accessPermissions
                  ?.canViewIncidents,
              code: err?.code,
              message: err?.message,
              error: err,
            }
          );

          setIncidents([]);

          setError(
            err?.message ||
              "Failed to load facility incidents."
          );
        }
      );

    return () => {
      console.log(
        "INCIDENTS LISTENER STOP",
        {
          tenantId,
          facilityId,
        }
      );

      unsubscribeIncidents();
    };
  }, [
    tenantId,
    facilityId,
    canViewShiftIncidents,
    accessPermissions
      ?.canViewIncidents,
    profile?.uid,
    profile?.email,
    allowedFacilityIds.join("|"),
  ]);

  useEffect(() => {
    if (!tenantId) {
      setCommunications([]);

      console.log(
        "COMMUNICATIONS LISTENER NOT STARTED — tenantId missing",
        {
          email: profile?.email,
          facilityId,
        }
      );

      return undefined;
    }

    if (!facilityId) {
      setCommunications([]);

      console.log(
        "COMMUNICATIONS LISTENER NOT STARTED — facilityId missing",
        {
          email: profile?.email,
          tenantId,
        }
      );

      return undefined;
    }

    if (
      !canViewShiftCommunications
    ) {
      setCommunications([]);

      console.log(
        "COMMUNICATIONS LISTENER NOT STARTED — permission denied by profile",
        {
          email: profile?.email,
          uid: profile?.uid,
          tenantId,
          facilityId,
          canViewShiftCommunications,
          accessPermission:
            accessPermissions
              ?.canViewCommunications,
        }
      );

      return undefined;
    }

    if (
      allowedFacilityIds.length > 0 &&
      !allowedFacilityIds.includes(
        facilityId
      )
    ) {
      setCommunications([]);

      console.error(
        "COMMUNICATIONS LISTENER BLOCKED — facility outside allowed scope",
        {
          email: profile?.email,
          tenantId,
          facilityId,
          allowedFacilityIds,
        }
      );

      return undefined;
    }

    console.log(
      "COMMUNICATIONS LISTENER START",
      {
        email: profile?.email,
        uid: profile?.uid,
        tenantId,
        facilityId,
        canViewShiftCommunications,
        accessPermission:
          accessPermissions
            ?.canViewCommunications,
      }
    );

    const communicationsQuery = query(
      collection(
        db,
        "communications"
      ),

      where(
        "tenantId",
        "==",
        tenantId
      ),

      where(
        "facilityId",
        "==",
        facilityId
      )
    );

    const unsubscribeCommunications =
      onSnapshot(
        communicationsQuery,
        (snap) => {
          const rows = snap.docs
            .map(
              (
                communicationDoc
              ) => ({
                id:
                  communicationDoc.id,
                ...communicationDoc.data(),
              })
            )
            .sort((a, b) => {
              const aTime = toMillis(
                a.sentAt ||
                  a.deliveredAt ||
                  a.createdAt ||
                  a.updatedAt
              );

              const bTime = toMillis(
                b.sentAt ||
                  b.deliveredAt ||
                  b.createdAt ||
                  b.updatedAt
              );

              return (
                (Number.isFinite(bTime)
                  ? bTime
                  : 0) -
                (Number.isFinite(aTime)
                  ? aTime
                  : 0)
              );
            });

          console.log(
            "COMMUNICATIONS LISTENER SUCCESS",
            {
              tenantId,
              facilityId,
              size: rows.length,
            }
          );

          setCommunications(rows);
        },
        (err) => {
          console.error(
            "SHIFT 360 COMMUNICATIONS LISTENER ERROR",
            {
              email:
                profile?.email,
              uid: profile?.uid,
              tenantId,
              facilityId,
              allowedFacilityIds,
              canViewShiftCommunications,
              accessPermission:
                accessPermissions
                  ?.canViewCommunications,
              code: err?.code,
              message: err?.message,
              error: err,
            }
          );

          setCommunications([]);

          setError(
            err?.message ||
              "Failed to load facility communications."
          );
        }
      );

    return () => {
      console.log(
        "COMMUNICATIONS LISTENER STOP",
        {
          tenantId,
          facilityId,
        }
      );

      unsubscribeCommunications();
    };
  }, [
    tenantId,
    facilityId,
    canViewShiftCommunications,
    accessPermissions
      ?.canViewCommunications,
    profile?.uid,
    profile?.email,
    allowedFacilityIds.join("|"),
  ]);

  useEffect(() => {
    const preset =
      SHIFT_PRESETS.find(
        (p) =>
          p.key === newShiftPreset
      ) || SHIFT_PRESETS[0];

    const base =
      new Date(selectedDayMs);

    let start = null;
    let end = null;

    if (!preset.isFlexibleTime) {
      start = new Date(base);

      start.setHours(
        preset.startHour,
        0,
        0,
        0
      );

      end = new Date(base);

      if (
        preset.overnight ||
        preset.endHour <=
          preset.startHour
      ) {
        end.setDate(
          end.getDate() + 1
        );
      }

      end.setHours(
        preset.endHour,
        0,
        0,
        0
      );
    }

    if (start && end) {
      setNewShiftStart(
        toLocalInputValue(start)
      );

      setNewShiftEnd(
        toLocalInputValue(end)
      );
    } else {
      setNewShiftStart("");
      setNewShiftEnd("");
    }
  }, [
    selectedDayMs,
    newShiftPreset,
  ]);

  const workersNormalized =
    useMemo(() => {
      return workers.map((w) => {
        const summary =
          normalizeWorkerCredSummary(
            w
          );

        return {
          ...w,
          _allCreds:
            summary.allCreds,
          _validTypes:
            summary.validTypes,
          _hasValidCred:
            summary.hasValidCred,
        };
      });
    }, [workers]);

  const workersByUid =
    useMemo(() => {
      const m = new Map();

      for (
        const w of
        workersNormalized
      ) {
        m.set(w.uid, w);
      }

      return m;
    }, [workersNormalized]);

  const roleOptions =
    useMemo(() => {
      const base =
        allowedRoles.length
          ? allowedRoles
          : DEFAULT_ROLES;

      const normalized =
        Array.from(
          new Map(
            [...base, ...shifts.filter(shift => shift.tenantId === tenantId && shift.facilityId === facilityId).map(shift => shift.role).filter(Boolean)].map((r) => [
              roleKey(r),
              r,
            ])
          ).entries()
        );

      const orderedKeys = [
        ...DISPLAY_ROLE_ORDER.filter(
          (rk) =>
            normalized.some(
              ([key]) =>
                key === rk
            )
        ),

        ...normalized
          .map(([key]) => key)
          .filter(
            (key) =>
              !DISPLAY_ROLE_ORDER.includes(
                key
              )
          ),
      ];

      const map =
        new Map(normalized);

      return {
        map,
        keys: orderedKeys,
      };
    }, [allowedRoles, shifts, tenantId, facilityId]);

  const visibleRoleKeys =
    useMemo(() => {
      return roleOptions.keys.filter(
        (rk) =>
          !hiddenRoleKeys.includes(
            rk
          )
      );
    }, [
      roleOptions.keys,
      hiddenRoleKeys,
    ]);

  const [
    autoNoShowEnabled,
    setAutoNoShowEnabled,
  ] = useState(false);

  useEffect(() => {
    if (
      !mobileBoardRole &&
      visibleRoleKeys.length
    ) {
      setMobileBoardRole(
        visibleRoleKeys[0]
      );
    }
  }, [
    mobileBoardRole,
    visibleRoleKeys,
  ]);

  useEffect(() => {
    if (
      mobileBoardRole &&
      !visibleRoleKeys.includes(
        mobileBoardRole
      )
    ) {
      setMobileBoardRole(
        visibleRoleKeys[0] || ""
      );
    }
  }, [
    mobileBoardRole,
    visibleRoleKeys,
  ]);

  const shiftsActive =
    useMemo(() => {
      return shifts.filter((s) => {
        const status = String(
          s.status || ""
        ).toLowerCase();

        if (
          !showClosed &&
          [
            "cancelled",
            "closed_unfilled",
          ].includes(status)
        ) {
          return false;
        }

        return true;
      });
    }, [shifts, showClosed]);

  const selectedShift =
    useMemo(
      () =>
        shiftsActive.find(
          (s) =>
            s.id === selectedShiftId
        ) || null,
      [
        selectedShiftId,
        shiftsActive,
      ]
    );

  const selectedShiftEligibilityResults =
    useMemo(() => {
      if (!selectedShift) {
        return [];
      }

      return getShiftEligibilityResults({
        workers:
          workersNormalized,

        shift:
          selectedShift,

        allShifts:
          shiftsActive,

        roleCheck: (
          worker,
          shift
        ) =>
          workerCanFillRole(
            worker,
            shift?.role
          ),

        credentialCheck: (
          worker
        ) => {
          const compliance =
            getWorkerCompliance(
              worker || {}
            );

          return (
            compliance?.canWork ===
            true
          );
        },

        availabilityCheck: (
          worker
        ) =>
          !isWorkerAvailabilityBlocked(
            worker
          ),

        overlapCheck: (
          worker,
          shift,
          allShifts
        ) => {
          const workerUid =
            worker?.uid ||
            worker?.id ||
            "";

          if (!workerUid) {
            return true;
          }

          const startMs =
            getShiftStartMs(
              shift
            );

          const endMs =
            getShiftEndMs(
              shift
            );

          if (
            !Number.isFinite(
              startMs
            ) ||
            !Number.isFinite(
              endMs
            ) ||
            endMs <= startMs
          ) {
            return true;
          }

          return workerHasConflict(
            workerUid,
            new Date(startMs),
            new Date(endMs),
            allShifts,
            shift?.id
          );
        },
      });
    }, [
      workersNormalized,
      selectedShift,
      shiftsActive,
    ]);

  const selectedShiftEligibleWorkers =
    useMemo(
      () =>
        selectedShiftEligibilityResults
          .filter(
            (result) =>
              result?.eligible ===
              true
          )
          .map(
            (result) =>
              result.worker
          ),
      [
        selectedShiftEligibilityResults,
      ]
    );

  const selectedShiftEligibilitySummary =
    useMemo(
      () =>
        summarizeShiftEligibility(
          selectedShiftEligibilityResults
        ),
      [
        selectedShiftEligibilityResults,
      ]
    );

  useEffect(() => {
    if (!selectedShift) {
      return;
    }

    console.log(
      "PLANNER SHIFT ELIGIBILITY",
      {
        shiftId:
          selectedShift?.id,

        role:
          selectedShift?.role,

        operationalWorkers:
          workersNormalized.length,

        eligibleWorkers:
          selectedShiftEligibleWorkers.length,

        summary:
          selectedShiftEligibilitySummary,
      }
    );
  }, [
    selectedShift,
    workersNormalized,
    selectedShiftEligibleWorkers,
    selectedShiftEligibilitySummary,
  ]);

  function isLateNoShowCandidate(
    shift
  ) {
    const status = String(
      shift?.status || ""
    ).toLowerCase();

    const eligibleStatuses = [
      "assigned",
      "activated",
      "tracking",
    ];

    if (
      !eligibleStatuses.includes(
        status
      )
    ) {
      return false;
    }

    const assignedWorkerUid =
      shift?.assignedWorkerUid ||
      shift?.workerUid ||
      shift?.workerId ||
      shift?.assignedWorkerId ||
      "";

    if (!assignedWorkerUid) {
      return false;
    }

 const startMs =
  getShiftStartMs(
    shift
  );

    if (
      !Number.isFinite(startMs)
    ) {
      return false;
    }

    const nowMs =
      Date.now();

    const minutesLate =
      Math.floor(
        (nowMs - startMs) /
          60000
      );

    if (minutesLate < 0) {
      return false;
    }

    const activated =
      shift?.activated === true ||
      Boolean(
        shift?.activatedAt
      ) ||
      status === "activated";

    const tracking =
      shift?.tracking === true ||
      status === "tracking";

    const arrived =
      shift?.arrived === true ||
      Boolean(
        shift?.arrivedAt
      ) ||
      status === "arrived";

    if (arrived) {
      return false;
    }

    if (
      status === "assigned" &&
      !activated &&
      !tracking &&
      minutesLate >= 15
    ) {
      return true;
    }

    if (
      (activated || tracking) &&
      minutesLate >= 30
    ) {
      return true;
    }

    return false;
  }

  const noShowAlerts =
    useMemo(() => {
      return (shiftsActive || [])
        .filter((s) => {
          const status = String(
            s.status || ""
          ).toLowerCase();

          if (
            ![
              "assigned",
              "activated",
              "tracking",
            ].includes(status)
          ) {
            return false;
          }

          return isLateNoShowCandidate(
            s
          );
        })
        .slice(0, 5);
    }, [shiftsActive]);

  const autoNoShowCandidates =
    useMemo(() => {
      return (
        shiftsActive || []
      ).filter(
        isLateNoShowCandidate
      );
    }, [shiftsActive]);

  const lateShiftAlerts =
    useMemo(() => {
      const nowMs =
        Date.now();

      return (
        shiftsActive || []
      ).filter((s) => {
        const status = String(
          s.status || ""
        ).toLowerCase();

        if (
          status !== "assigned"
        ) {
          return false;
        }

        const startMs =
          getShiftStartMs(
            s
          );

        if (
          !Number.isFinite(
            startMs
          )
        ) {
          return false;
        }

        return (
          nowMs >
          startMs +
            15 * 60 * 1000
        );
      });
    }, [shiftsActive]);

 
  useEffect(() => {
    if (!autoNoShowEnabled) {
      return;
    }

    if (
      !autoNoShowCandidates.length
    ) {
      return;
    }

    autoNoShowCandidates.forEach(
      async (shift) => {
        try {
          await setShiftNoShow(
            shift.id,
            auth.currentUser?.uid ||
              "planner",
            "Auto no-show: worker not arrived after grace period"
          );
        } catch (err) {
          // Canonical no-show service
          // remains authoritative.
        }
      }
    );
  }, [
    autoNoShowEnabled,
    autoNoShowCandidates,
  ]);

  useEffect(() => {
    if (!selectedShift) {
      setEditStartTime("");
      setEditEndTime("");
      setEditWorkerUid("");
      setShift360Section("operations");

      return;
    }

    setShift360Section("operations");

   const s =
  getShiftStartMs(
    selectedShift
  );

const e =
  getShiftEndMs(
    selectedShift
  );

    setEditStartTime(
      Number.isFinite(s)
        && selectedShift.timeZone ? zonedInputValue(s, selectedShift.timeZone)
        : ""
    );

    setEditEndTime(
      Number.isFinite(e)
        && selectedShift.timeZone ? zonedInputValue(e, selectedShift.timeZone)
        : ""
    );

    setEditWorkerUid(
      getAssignedWorkerUid(
        selectedShift
      ) || ""
    );
  }, [selectedShift]);

  const visibleCalendarDays =
    useMemo(() => {
      const start =
        startOfDayMs(
          calendarAnchorMs
        );

      const startDate =
        new Date(start);

      const endOfMonth =
        new Date(
          startDate.getFullYear(),
          startDate.getMonth() +
            1,
          0
        );

      endOfMonth.setHours(
        0,
        0,
        0,
        0
      );

      const days = [];
      let cursor = start;

      while (
        cursor <=
        endOfMonth.getTime()
      ) {
        days.push({
          ms: cursor,

          key:
            ymd(cursor),

          label:
            new Date(
              cursor
            ).toLocaleDateString(
              undefined,
              {
                weekday:
                  "short",
              }
            ),

          dateLabel:
            new Date(
              cursor
            ).toLocaleDateString(
              undefined,
              {
                month: "short",
                day: "numeric",
              }
            ),
        });

        cursor =
          addDays(
            cursor,
            1
          );
      }

    return days;
  }, [calendarAnchorMs]);

const shiftsForSelectedDay = useMemo(() => selectDailyShifts(shiftsActive, selectedDayMs, facilityTimeZone)
  .filter(shift => !['completed','no_show','cancelled','canceled','closed_unfilled'].includes(String(shift.status || '').toLowerCase()))
  .sort((a,b) => toMillis(a.startTime)-toMillis(b.startTime)), [selectedDayMs, shiftsActive, facilityTimeZone]);

const pastShiftsForSelectedDay = useMemo(() => selectDailyShifts(shiftsActive, selectedDayMs, facilityTimeZone)
  .filter(shift => hasShiftEnded(shift, now) || ['completed','no_show','cancelled','canceled','closed_unfilled'].includes(String(shift.status || '').toLowerCase()))
  .sort((a,b) => toMillis(b.endTime)-toMillis(a.endTime)), [selectedDayMs, shiftsActive, now, facilityTimeZone]);

const pendingRequests = useMemo(() => {
  const rows = shiftsForSelectedDay
    .filter((s) => {
      const modeName = getShiftMode(s);
      const statusName = String(s.status || "").toLowerCase();
      const assignedUid = getAssignedWorkerUid(s);
      const req = Array.isArray(s.requestedWorkerUids)
        ? s.requestedWorkerUids.filter(Boolean)
        : [];

      return (
        modeName === "pool" &&
        statusName === "open" &&
        !assignedUid &&
        req.length > 0
      );
    })
    .flatMap((s) => {
      const req = Array.isArray(s.requestedWorkerUids)
        ? s.requestedWorkerUids.filter(Boolean)
        : [];

      return req.map((uid) => ({
        shiftId: s.id,
        shift: s,
        workerUid: uid,
      }));
    });

  return rows.sort((a, b) => toMillis(a.shift.startTime) - toMillis(b.shift.startTime));
}, [shiftsForSelectedDay]);

  const offeredShifts = useMemo(() => {
    return shiftsForSelectedDay
      .filter((s) => {
        const modeName = getShiftMode(s);
        const statusName = String(s.status || "").toLowerCase();
        const assignedUid = getAssignedWorkerUid(s);
        const arrived = isShiftArrived(s);

        if (modeName !== "direct") return false;
        if (!["offered", "open"].includes(statusName)) return false;
        if (assignedUid) return false;
        if (arrived) return false;

        return true;
      })
      .sort((a, b) => toMillis(a.startTime) - toMillis(b.startTime));
  }, [shiftsForSelectedDay]);

  const workerPool = useMemo(() => {
    const qtxt = String(searchTerm || "").trim().toLowerCase();

    const selectedRoleFilter = matchSelectedShiftRole
      ? roleKey(selectedShift?.role || newShiftRole)
      : roleKey(newShiftRole);

    const currentFacility =
      typeof facilityProfile === "object" && facilityProfile !== null
        ? facilityProfile
        : {};

    const facilityCity = String(currentFacility?.city || "").trim().toLowerCase();
    const facilityState = String(currentFacility?.state || "TX").trim().toUpperCase();
    const facilityZip = String(currentFacility?.zipCode || currentFacility?.zip || "").trim();

    function geoScore(worker) {
      const workerZip = String(worker.zipCode || worker.zip || "").trim();
      const workerCity = String(worker.city || "").trim().toLowerCase();
      const workerState = String(worker.state || "TX").trim().toUpperCase();

      if (facilityZip && workerZip && facilityZip === workerZip) return 0;
      if (facilityCity && workerCity && facilityCity === workerCity) return 1;
      if (facilityState && workerState && facilityState === workerState) return 2;

      return 3;
    }

    return workersNormalized
  .filter((w) => {
    const compliance = getWorkerCompliance(w);

    const role = String(w.platformRole || w.role || "").toLowerCase();
    const status = String(w.status || "active").toLowerCase();
    const inactive = status === "inactive";

    const onboardingStatus = String(w.onboardingStatus || "").toLowerCase();
    const workerStatus = String(w.workerStatus || "").toLowerCase();
    const workerOperationalState = String(w.workerOperationalState || "").toUpperCase();
    const complianceStatus = String(w.complianceStatus || "").toLowerCase();

    const adminWorkforceReady =
      role === "worker" &&
      status === "active" &&
      (
        compliance.canWork ||
        onboardingStatus === "approved" ||
        workerStatus === "ready" ||
        workerOperationalState === "READY" ||
        complianceStatus === "approved" ||
        complianceStatus === "ready" ||
        w.onboardingCompleted === true
      );

    if (!adminWorkforceReady) return false;

    const workerAssignedNow = (shiftsActive || []).some((s) => {
      const assigned =
        s.assignedWorkerUid === w.uid ||
        s.workerUid === w.uid ||
        s.workerId === w.uid ||
        (Array.isArray(s.assignedWorkerUids) &&
          s.assignedWorkerUids.includes(w.uid));

      return (
        assigned &&
        ["assigned", "activated", "tracking", "arrived"].includes(
          String(s.status || "").toLowerCase()
        )
      );
    });

    const availabilityBlocked = isWorkerAvailabilityBlocked(w);

const workerUnavailable =
  inactive ||
  workerOperationalState === "ACTION_REQUIRED" ||
  workerOperationalState === "SUSPENDED";

if (staffPoolTab === "ready" && workerAssignedNow) {
  return false;
}

if (staffPoolTab === "assigned" && !workerAssignedNow) {
  return false;
}

if (staffPoolTab === "unavailable" && !workerUnavailable && !availabilityBlocked) {
  return false;
}

    if (qtxt) {
      const credentialText = [
        ...(Array.isArray(w.positions) ? w.positions : []),
        ...(Array.isArray(w._validTypes) ? w._validTypes : []),
        w.role,
        w.workerType,
        w.roleType,
        w.position,
        w.primaryRole,
        w.licenseType,
        w.credentialClass,
        w.certification,
        w.fullName,
        w.name,
        w.firstName,
        w.lastName,
      ]
        .filter(Boolean)
        .join(" ");

      const hay = `
        ${w.email || ""}
        ${w.uid || ""}
        ${w.fullName || ""}
        ${w.name || ""}
        ${w.firstName || ""}
        ${w.lastName || ""}
        ${w.city || ""}
        ${w.state || ""}
        ${w.zipCode || ""}
        ${credentialText}
      `.toLowerCase();

      if (!hay.includes(qtxt)) return false;
    }

    if (!showDisabledWorkers && inactive) return false;

    if (poolFilter === "accredited") {
      if (!w._hasValidCred) return false;
      if (status !== "active") return false;
    }

    if (
  matchSelectedShiftRole &&
  selectedRoleFilter &&
  !workerCanFillRole(w, selectedRoleFilter)
) {
  return false;
}

    return true;
  })
  .sort((a, b) => {
    const ga = geoScore(a);
    const gb = geoScore(b);

    const reliabilityA = calculateReliabilityScore(a);
    const reliabilityB = calculateReliabilityScore(b);

    const scoreA = reliabilityA - ga * 10;
    const scoreB = reliabilityB - gb * 10;

    if (scoreA !== scoreB) return scoreB - scoreA;

    return String(a.email || a.uid).localeCompare(String(b.email || b.uid));
  });

  }, [
  workersNormalized,
  searchTerm,
  matchSelectedShiftRole,
  selectedShift,
  newShiftRole,
  facilityProfile,
  showDisabledWorkers,
  poolFilter,
  staffPoolTab,
  shiftsActive,
]);

  const dailyModel = useMemo(() => buildDailyBoard({
    shifts: shifts.filter(s => s.tenantId === tenantId && s.facilityId === facilityId),
    selectedDayMs, now, timeZone: facilityTimeZone, roles: roleOptions.keys, presets: SHIFT_PRESETS,
    workerIssuesByUid: Object.fromEntries(workersNormalized.map(worker => [worker.uid || worker.id,
      getWorkerCompliance(worker || {}).canWork !== true ? "Worker compliance requires review"
        : isWorkerAvailabilityBlocked(worker) ? "Worker is unavailable or blocked" : ""])),
  }), [shifts, tenantId, facilityId, selectedDayMs, now, facilityTimeZone, roleOptions.keys, workersNormalized]);

  const dailyAvailable = useMemo(() => {
    const byUid = new Map(), byRole = {};
    for (const row of dailyModel.open) {
      if (row.summary.assigned) continue;
      const results = getShiftEligibilityResults({
        workers: workersNormalized, shift: row.shift, allShifts: shiftsActive,
        roleCheck: (worker, shift) => workerCanFillRole(worker, shift.role),
        credentialCheck: worker => getWorkerCompliance(worker || {}).canWork === true,
        availabilityCheck: worker => !isWorkerAvailabilityBlocked(worker),
        overlapCheck: (worker, shift, allShifts) => {
          const start = getShiftStartMs(shift), end = getShiftEndMs(shift);
          if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return true;
          return workerHasConflict(worker.uid || worker.id, new Date(start), new Date(end), allShifts, shift.id);
        },
      });
      if (!byRole[row.role]) byRole[row.role] = new Set();
      results.filter(r => r.eligible).forEach(r => {
        byUid.set(r.workerUid, r.worker);
        byRole[row.role].add(r.workerUid);
      });
    }
    return { workers: [...byUid.values()], roles: Object.fromEntries(Object.entries(byRole).map(([role, ids]) => [role, ids.size])) };
  }, [dailyModel, workersNormalized, shiftsActive]);

  const filteredShiftsForBoard = useMemo(() => {
    if (scheduleFocus) return dailyModel.rows.filter(row =>
      (!scheduleFocus.role || row.role === scheduleFocus.role) &&
      (!scheduleFocus.window || row.window.key === scheduleFocus.window)
    ).map(row => row.shift);

    if (lifecycleFilter === "completed") return dailyModel.stages.completed.map(row => row.shift);
    if (!lifecycleFilter) return shiftsForSelectedDay;

    return shiftsForSelectedDay.filter((s) => {
      const status = String(s.status || "open").toLowerCase();
      return status === lifecycleFilter;
    });
  }, [shiftsForSelectedDay, lifecycleFilter, scheduleFocus, dailyModel]);

  const dailyByBlockRole = useMemo(() => {
    const out = {};

    for (const block of BOARD_BLOCKS) {
      out[block.key] = {};

      for (const rk of roleOptions.keys) {
        out[block.key][rk] = [];
      }
    }

    for (const s of filteredShiftsForBoard) {
      if (
        lifecycleFilter &&
        String(s.status || "open").toLowerCase() !== lifecycleFilter
      ) {
        continue;
      }
      const rk = roleKey(s.role);

      if (s.isFlexibleTime) {
        if (!out["06_14"][rk]) out["06_14"][rk] = [];
        out["06_14"][rk].push(s);
        continue;
      }

      const st = toMillis(s.startTime);
      if (!Number.isFinite(st)) continue;

      if (!s.timeZone) continue;
      const h = Number(zonedParts(st, s.timeZone).hour);

      let blockKey = "06_14";

      if (h >= 14 && h < 18) blockKey = "14_22";
      if (h === 18) blockKey = "18_06";
      if (h === 19) blockKey = "19_07";
      if (h >= 20 || h < 6) blockKey = "22_06";

      if (!out[blockKey][rk]) out[blockKey][rk] = [];
      out[blockKey][rk].push(s);
    }

    for (const block of BOARD_BLOCKS) {
      for (const rk of roleOptions.keys) {
        out[block.key][rk] = (out[block.key][rk] || []).sort((a, b) => {
          if (a?.isFlexibleTime && b?.isFlexibleTime) return 0;
          if (a?.isFlexibleTime) return -1;
          if (b?.isFlexibleTime) return 1;
          return toMillis(a.startTime) - toMillis(b.startTime);
        });
      }
    }

return out;
}, [roleOptions.keys, filteredShiftsForBoard]);

const schedulerConflictSummary = useMemo(() => {
  if (!facilityId || !newShiftRole || !newShiftStart || !newShiftEnd) {
    return "";
  }

  const preset =
    SHIFT_PRESETS.find((p) => p.key === newShiftPreset) || SHIFT_PRESETS[0];

  const isFlexible = preset?.isFlexibleTime === true;

  const selectedDay = new Date(selectedDayMs);

  let startDate, endDate;
  try {
    const interval = regionalShiftInterval(toLocalInputValue(selectedDay).slice(0,10), newShiftStart, newShiftEnd, facilityTimeZone);
    startDate = interval.start; endDate = interval.end;
  } catch (error) { return error.message; }

  if (!Number.isFinite(startDate.getTime()) || !Number.isFinite(endDate.getTime())) {
    return "Invalid date/time.";
  }

  if (endDate.getTime() <= startDate.getTime() && !isFlexible) {
    return "End must be after start.";
  }

  const nowMs = Date.now();

  if (endDate.getTime() <= nowMs) {
    return "This shift has already ended. Choose a future time or move the calendar to the next day.";
  }

  if (startDate.getTime() <= nowMs && !isFlexible) {
    return "This shift has already started. Use Flexible Emergency Fill only if this is an active urgent coverage need.";
  }

  if (!isFlexible && String(newShiftMode).toLowerCase() === "direct") {
    const selectedWorkers =
      Array.isArray(selectedDirectWorkerUids) && selectedDirectWorkerUids.length > 0
        ? selectedDirectWorkerUids.filter(Boolean)
        : selectedWorkerUid
          ? [selectedWorkerUid]
          : [];

    const conflicting = selectedWorkers.filter((uid) =>
      workerHasConflict(uid, startDate, endDate, shiftsActive)
    );

    if (conflicting.length > 0) {
      return "Selected worker already has an overlapping shift.";
    }
  }

  return "";
}, [
  facilityId,
  facilityTimeZone,
  newShiftRole,
  newShiftStart,
  newShiftEnd,
  newShiftMode,
  newShiftPreset,
  selectedDayMs,
  selectedWorkerUid,
  selectedDirectWorkerUids,
  shiftsActive,
]);

const boardMetrics = useMemo(() => {
  return getBoardMetrics(shiftsForSelectedDay);
}, [shiftsForSelectedDay]);

const { liveOpsMetrics } = usePlannerMetrics({
  shiftsActive,
  shiftsForSelectedDay,
  workerPool,
  boardMetrics,
  selectedDayMs,
  startOfDayMs,
  ymd,
  roleKey,
  isWorkerAvailabilityBlocked,
});

const applyPreset = useCallback(
  (presetKey) => {
    const preset =
      SHIFT_PRESETS.find((p) => p.key === presetKey) || SHIFT_PRESETS[0];

    const base = new Date(selectedDayMs);

    setNewShiftPreset(preset.key);

    const start = new Date(base);
    const end = new Date(base);

    if (preset.isFlexibleTime) {
      start.setHours(6, 0, 0, 0);
      end.setHours(14, 0, 0, 0);

      setNewShiftStart(toLocalInputValue(start));
      setNewShiftEnd(toLocalInputValue(end));
      return;
    }

    start.setHours(preset.startHour, 0, 0, 0);

    if (preset.overnight || preset.endHour <= preset.startHour) {
      end.setDate(end.getDate() + 1);
    }

    end.setHours(preset.endHour, 0, 0, 0);

    setNewShiftStart(toLocalInputValue(start));
    setNewShiftEnd(toLocalInputValue(end));
  },
  [selectedDayMs]
);

function openOverflow(title, shiftList) {
  const rows = Array.isArray(shiftList) ? shiftList : [];

  if (rows.length <= 4) return;

  setOverflowTitle(title || "Overflow shifts");
  setOverflowPanel(rows);
}

const handleCreateShift = async (autoAssignBest = false) => {
  try {
    if (!facilityId) {
      window.alert("ShiftBridge Assist: Select a facility before publishing a shift.");
      return;
    }

    const selectedRoleKey = roleKey(newShiftRole);

    if (!selectedRoleKey) {
      window.alert("ShiftBridge Assist: Choose a worker role before publishing.");
      return;
    }

    const selectedPreset =
      SHIFT_PRESETS.find((p) => p.key === newShiftPreset) || SHIFT_PRESETS[0];

    const selectedIsFlexible = selectedPreset?.isFlexibleTime === true;

    let finalShiftStart = newShiftStart;
    let finalShiftEnd = newShiftEnd;

    if (selectedIsFlexible && (!finalShiftStart || !finalShiftEnd)) {
      const base = new Date(selectedDayMs);

      const start = new Date(base);
      if (!facilityTimeZone) { alert("Configure the facility timezone before scheduling."); return; }
      start.setHours(Number(zonedParts(Date.now(), facilityTimeZone).hour), 0, 0, 0);

      const end = new Date(start);
      end.setHours(start.getHours() + 8);

      finalShiftStart = toLocalInputValue(start);
      finalShiftEnd = toLocalInputValue(end);

      setNewShiftStart(finalShiftStart);
      setNewShiftEnd(finalShiftEnd);
    }

    if (!finalShiftStart) {
      window.alert("ShiftBridge Assist: Choose a start date and time before publishing.");
      return;
    }

    if (!finalShiftEnd) {
      window.alert("ShiftBridge Assist: Choose an end date and time before publishing.");
      return;
    }

      const preset =
        SHIFT_PRESETS.find((p) => p.key === newShiftPreset) || SHIFT_PRESETS[0];

      const isFlexible = preset?.isFlexibleTime === true;

      console.log("START =", finalShiftStart);
      console.log("END =", finalShiftEnd);

     const selectedDay = new Date(selectedDayMs);

let startDate, endDate;
try {
  const interval = regionalShiftInterval(toLocalInputValue(selectedDay).slice(0,10), finalShiftStart, finalShiftEnd, facilityTimeZone);
  startDate = interval.start; endDate = interval.end;
} catch (error) { alert(error.message); return; }

console.log("START DATE =", startDate);
console.log("END DATE =", endDate);

      if (
        !Number.isFinite(startDate.getTime()) ||
        !Number.isFinite(endDate.getTime())
      ) {
        alert("Enter valid start and end times.");
        return;
      }

      if (endDate <= startDate) {
        alert("End time must be after start time.");
        return;
      }

      let autoWorker = null;

if (autoAssignBest) {
  autoWorker = workerPool.find((w) => {
if (!workerCanFillRole(w, selectedRoleKey)) {
  return false;
}

    
    if (isWorkerAvailabilityBlocked(w)) {
      return false;
    }

    return !workerHasConflict(w.uid, startDate, endDate, shiftsActive);
  });

  if (!autoWorker) {
    alert("No available matching worker found.");
    return;
  }
}

      const modeName = autoAssignBest
        ? "direct"
        : String(newShiftMode || "pool").toLowerCase();

      const qty = autoAssignBest ? 1 : Math.max(1, Number(createQty || 1));

      const directWorkerUid = String(selectedWorkerUid || "").trim();

      const offeredWorkerUids = autoAssignBest
        ? []
        : Array.isArray(selectedDirectWorkerUids) &&
          selectedDirectWorkerUids.length > 0
          ? selectedDirectWorkerUids.filter(Boolean)
          : directWorkerUid
            ? [directWorkerUid]
            : [];

      if (!autoAssignBest && modeName === "direct" && offeredWorkerUids.length === 0) {
        alert("Choose a worker for direct offer.");
        return;
      }

if (!autoAssignBest && modeName === "direct") {
  const unavailableWorker = offeredWorkerUids
    .map((uid) => workersByUid.get(uid))
    .find((worker) => isWorkerAvailabilityBlocked(worker));

  if (unavailableWorker) {
    const availabilityMeta = availabilityStatusMeta(unavailableWorker);

    const ok = window.confirm(
      `${unavailableWorker.email || unavailableWorker.uid} is marked ${availabilityMeta.label}. Send direct offer anyway?`
    );

    if (!ok) return;
  }

  const blockedWorker = offeredWorkerUids
    .map((uid) => workersByUid.get(uid))
    .find((worker) => {
      const compliance = getWorkerCompliance(worker || {});
      return !compliance.canWork;
    });

  if (blockedWorker) {
    const compliance = getWorkerCompliance(blockedWorker);

    alert(
      `${blockedWorker.email || blockedWorker.uid} cannot receive this offer. ${
        compliance.reason || "Worker is not compliant."
      }`
    );
    return;
  }
}

      const activeFacilityProfile =
        typeof facilityProfile === "object" && facilityProfile !== null
          ? facilityProfile
          : {};


      for (let i = 0; i < qty; i += 1) {
        const roleRates =
          activeFacilityProfile?.billingRates?.[selectedRoleKey] ||
          activeFacilityProfile?.billingRates?.[String(selectedRoleKey).toUpperCase()] ||
          {
            billRate: 0,
            payRate: 0,
          };

        const cleanBillRate = Number(roleRates.billRate || 0);
        const payload = buildShiftPayload({
      facilityProfile: activeFacilityProfile,

tenantId,

facilityId,

facilityName:
  activeFacilityProfile?.name ||
  activeFacilityProfile?.facilityName ||
  profile?.facilityName ||
  profile?.name ||
  facilityId,

          role: selectedRoleKey,

          status: autoAssignBest
            ? SHIFT_STATUS.ASSIGNED
            : modeName === "direct"
              ? SHIFT_STATUS.OFFERED
              : SHIFT_STATUS.OPEN,

          start: startDate,
          end: endDate,

          createdBy: auth.currentUser?.uid || "planner",

          workerId: autoAssignBest ? autoWorker.uid : "",
          workerName: autoAssignBest ? autoWorker.name || autoWorker.email || "" : "",

          assignedWorkerId: autoAssignBest ? autoWorker.uid : "",
          assignedWorkerName: autoAssignBest
            ? autoWorker.name || autoWorker.email || ""
            : "",

          offeredTo: autoAssignBest
            ? []
            : modeName === "direct"
              ? offeredWorkerUids
              : [],

          requestedBy: [],

          extra: {
            timeZone: facilityTimeZone,
            startTime: startDate,
            endTime: endDate,

            // Flexible Emergency is only a coverage type now.
            // It should NOT create Time Required when planner already picked time.
            isFlexible: isFlexible,
            flexibleEmergency: isFlexible,
            isFlexibleTime: false,
            timeRequired: false,
            timeConfirmed: true,

            timeSlot: isFlexible ? "Flexible Emergency Fill" : preset.label,
            coverageType: isFlexible ? "emergency_fill" : "scheduled",

            mode: modeName,
            assignMode: modeName,

            offeredWorkerUids: autoAssignBest
              ? []
              : modeName === "direct"
                ? offeredWorkerUids
                : [],

            assignedWorkerUid: autoAssignBest ? autoWorker.uid : "",
            assignedWorkerUids: autoAssignBest ? [autoWorker.uid] : [],

            requestedWorkerUids: [],
            declinedWorkerUids: [],

            autoAssigned: autoAssignBest,
          },
        });
        payload.billRate = cleanBillRate;
        payload.compensationRateVersion = activeFacilityProfile?.compensationRateVersion || null;

        console.log("CREATE SHIFT PAYLOAD", payload);

        await createShift(payload);
      }

      const createdDayMs = selectedDayMs;
      setSelectedDayMs(createdDayMs);
      setCalendarAnchorMs(createdDayMs);

      setSelectedWorkerUid("");
      setSelectedDirectWorkerUids([]);
      setCreateQty(1);

      alert(
        autoAssignBest
          ? `Shift auto-assigned to ${autoWorker.email || autoWorker.uid}.`
          : modeName === "direct"
            ? "Direct shift offer created."
            : `${qty} pool shift${qty === 1 ? "" : "s"} created.`
      );

      } catch (err) {
  console.error("handleCreateShift error:", {
    name: err?.name,
    code: err?.code,
    message: err?.message,
    stack: err?.stack,
    fullError: err,
  });

  alert(
    err?.message ||
      err?.code ||
      "Failed to create shift."
  );
}
  };

  const handleDeclineRequest = async (
  workerUid,
  shiftId
) => {
  if (!workerUid || !shiftId) {
    return;
  }

  const shift = shiftsActive.find(
    (s) => s.id === shiftId
  );

  if (!shift) {
    alert("Shift not found.");
    return;
  }

  const requested =
    Array.isArray(
      shift.requestedWorkerUids
    )
      ? shift.requestedWorkerUids.filter(
          Boolean
        )
      : [];

  const declined =
    Array.isArray(
      shift.declinedWorkerUids
    )
      ? shift.declinedWorkerUids.filter(
          Boolean
        )
      : [];

  if (
    !requested.includes(workerUid)
  ) {
    alert(
      "This request is no longer pending."
    );
    return;
  }

  const worker =
    workersByUid.get(workerUid);

  const workerLabel =
    worker?.email ||
    worker?.displayName ||
    workerUid;

  const confirmed =
    window.confirm(
      `Decline the shift request from ${workerLabel}?`
    );

  if (!confirmed) {
    return;
  }

  try {
await declinePoolRequest(
  shiftId,
  workerUid
);

    alert(
      "Shift request declined."
    );
  } catch (err) {
    console.error(
      "handleDeclineRequest error:",
      err
    );

    alert(
      err?.message ||
        "Unable to decline request."
    );
  }
};


const handleApproveRequest = async (
  workerUid,
  shiftIdOverride = null
) => {
  const shiftId =
    shiftIdOverride ||
    selectedShiftId;

  if (!shiftId) {
    return alert(
      "Select a shift first."
    );
  }

  const shift =
    shiftsActive.find(
      (s) => s.id === shiftId
    );

  if (!shift) {
    return alert(
      "Shift not found."
    );
  }

  const worker =
    workersByUid.get(workerUid);

  if (!worker) {
    alert(
      "Worker record not found."
    );
    return;
  }

  if (
    isWorkerAvailabilityBlocked(
      worker
    )
  ) {
    const availabilityMeta =
      availabilityStatusMeta(
        worker
      );

    const ok =
      window.confirm(
        `${worker.email || worker.uid} is marked ${availabilityMeta.label}. Assign Worker anyway?`
      );

    if (!ok) {
      return;
    }
  }

  const compliance =
    getWorkerCompliance(
      worker || {}
    );

  if (!compliance.canWork) {
    alert(
      compliance.reason ||
        "Worker is not compliant and cannot be approved."
    );

    return;
  }

  const shiftMode =
    getShiftMode(shift);

  if (
    shiftMode !== "pool"
  ) {
    return alert(
      "Selected shift is not a pool shift."
    );
  }

  const shiftStatus =
    String(
      shift.status || ""
    )
      .trim()
      .toLowerCase();

  if (
    shiftStatus !== "open"
  ) {
    return alert(
      "Shift must be open."
    );
  }

  const approvedStartMs =
    toMillis(
      shift.startTime
    );

  const approvedEndMs =
    toMillis(
      shift.endTime
    );

  if (
    !Number.isFinite(
      approvedStartMs
    ) ||
    !Number.isFinite(
      approvedEndMs
    )
  ) {
    return alert(
      "Shift time is invalid."
    );
  }

  const hasWorkerConflict =
    workerHasConflict(
      workerUid,
      approvedStartMs,
      approvedEndMs,
      shiftsActive,
      shift.id
    );

  if (hasWorkerConflict) {
    alert(
      "This worker already has another overlapping assigned shift."
    );

    return;
  }

  try {
    await approvePoolRequest(
      shiftId,
      workerUid,
      "planner_approved_request"
    );

    alert(
      "Approved request → Assigned, and overlapping requests cleared."
    );
  } catch (err) {
    console.error(
      "approvePoolRequest error:",
      err
    );

    alert(
      err?.message ||
        "Approve failed."
    );
  }
};

  const canEditShiftTime = (shift) => {
    if (!shift) return false;

    const s = String(shift.status || "").toLowerCase();
    const mode = getShiftMode(shift);

    if (mode === "direct" && ["open", "offered"].includes(s)) {
      return true;
    }

    if (shift.isFlexibleTime === true) {
      return true;
    }

    if (hasShiftEnded(shift, now)) return false;

    return ["open", "offered", "assigned"].includes(s);
  };

  const canDeleteShift = (shift) => {
    if (!shift) return false;

    const s = String(shift.status || "").toLowerCase();
    const mode = getShiftMode(shift);

    if (mode === "direct" && ["open", "offered"].includes(s)) {
      return true;
    }

    if (shift.isFlexibleTime === true && ["open", "offered"].includes(s)) {
      return true;
    }

    if (hasShiftEnded(shift, now)) return false;

    return ["open", "offered"].includes(s);
  };

  function applySetTimePresetToSelected(presetKey) {
    if (!selectedShift) return;

    const preset = SHIFT_PRESETS.find((p) => p.key === presetKey) || SHIFT_PRESETS[0];
    setSetTimePreset(preset.key);

    if (preset.isFlexibleTime) return;

    const base = new Date(selectedDayMs);

    const start = new Date(base);
    start.setHours(preset.startHour, 0, 0, 0);

    const end = new Date(base);
    if (preset.overnight || preset.endHour <= preset.startHour) {
      end.setDate(end.getDate() + 1);
    }
    end.setHours(preset.endHour, 0, 0, 0);

    setEditStartTime(toLocalInputValue(start));
    setEditEndTime(toLocalInputValue(end));
  }

const handleSaveShiftTime = async () => {
  if (!selectedShift) {
    alert(
      "Select a shift first."
    );

    return;
  }

  if (
    !canEditShiftTime(
      selectedShift
    )
  ) {
    alert(
      "This shift can no longer be edited."
    );

    return;
  }

  let newStart, newEnd;
  try {
    const zone = resolveShiftTimeZone(selectedShift, facilityProfile, {timeZone: tenantTimeZone});
    newStart = localDateTimeToInstant(editStartTime, zone);
    newEnd = localDateTimeToInstant(editEndTime, zone);
  } catch (error) { alert(error.message); return; }

  if (
    !Number.isFinite(
      newStart.getTime()
    ) ||
    !Number.isFinite(
      newEnd.getTime()
    )
  ) {
    alert(
      "Invalid start or end."
    );

    return;
  }

  if (
    newEnd.getTime() <=
    newStart.getTime()
  ) {
    alert(
      "End must be after start."
    );

    return;
  }

  /*
   * Planner-side visual conflict checks remain
   * for fast feedback.
   *
   * Domain-level worker overlap validation still
   * runs again inside confirmShiftTime().
   */
  if (
    shiftConflicts({
      start: newStart,
      end: newEnd,
      role:
        selectedShift.role,
      facilityId,
      shifts:
        shiftsActive,
      ignoreShiftId:
        selectedShift.id,
    })
  ) {
    alert(
      "This edit overlaps an existing shift for the same role."
    );

    return;
  }

  const assignedUid =
    editWorkerUid ||
    getAssignedWorkerUid(
      selectedShift
    );

  if (
    assignedUid &&
    workerHasConflict(
      assignedUid,
      newStart,
      newEnd,
      shiftsActive,
      selectedShift.id
    )
  ) {
    alert(
      "Assigned worker has an overlapping shift."
    );

    return;
  }

  const isFlexible =
    selectedShift
      .isFlexibleTime ===
    true;

  const timeSlot =
    SHIFT_PRESETS.find(
      (preset) =>
        preset.key ===
        setTimePreset
    )?.label ||
    "Custom";

  try {
    await confirmShiftTime({
      shiftId:
        selectedShift.id,

      startTime:
        newStart,

      endTime:
        newEnd,

      actorUid:
        auth.currentUser?.uid ||
        "planner",

      timeSlot,

      assignedWorkerUid:
        assignedUid || "",
    });

    alert(
      isFlexible
        ? assignedUid
          ? "Flexible shift converted to assigned timed shift."
          : "Flexible shift converted to open timed shift."
        : "Shift time updated."
    );
  } catch (err) {
    console.error(
      "update shift time error:",
      err
    );

    alert(
      err?.message ||
        "Failed to update shift time."
    );
  }
};

  const handleReassignSelectedShift = async () => {
    if (!selectedShift) return alert("Select a shift first.");
    if (!editWorkerUid) return alert("Choose a worker.");

    const startDate = new Date(selectedShift.startTime);
    const endDate = new Date(selectedShift.endTime);

const worker = workersByUid.get(editWorkerUid);

if (!worker) {
  alert("Worker record not found.");
  return;
}
if (isWorkerAvailabilityBlocked(worker)) {
  const availabilityMeta = availabilityStatusMeta(worker);

  const ok = window.confirm(
    `${worker.email || worker.uid} is marked ${availabilityMeta.label}. Assign anyway?`
  );

  if (!ok) return;
}

const compliance = getWorkerCompliance(worker || {});


    if (!compliance.canWork) {
      alert(compliance.reason || "Worker is not compliant and cannot be assigned.");
      return;
    }
    const hasWorkerConflict = workerHasConflict(
      editWorkerUid,
      startDate,
      endDate,
      shiftsActive,
      selectedShift.id
    );

    if (hasWorkerConflict) {
      alert("This worker already has another overlapping assigned shift.");
      return;
    }

    try {
      await reassignWorkerOnShift(selectedShift.id, editWorkerUid, "planner_reassign");
      alert("Shift reassigned.");
    } catch (err) {
      console.error("reassignWorkerOnShift error:", err);
      alert(err?.message || "Failed to reassign shift.");
    }
  };
  const handleAutoAssignBest = async () => {
    if (!selectedShift) return alert("Select a shift first.");

    const role = roleKey(selectedShift.role);
    const startDate = new Date(selectedShift.startTime);
    const endDate = new Date(selectedShift.endTime);

    const currentFacility =
      typeof facilityProfile === "object" && facilityProfile !== null
        ? facilityProfile
        : {};

    const facilityCity = String(currentFacility?.city || "").trim().toLowerCase();
    const facilityState = String(currentFacility?.state || "TX").trim().toUpperCase();
    const facilityZip = String(currentFacility?.zipCode || currentFacility?.zip || "").trim();

    function scoreWorker(worker) {
      let score = 0;
      const smart = buildSmartMatch(worker, selectedShift);

      score += smart.score;

      const reliabilityScore = calculateReliabilityScore(worker, shiftsActive)
      score += reliabilityScore;

 const isQualifiedForRole = workerCanFillRole(worker, role);

if (isQualifiedForRole) {
  score += 40;
}

      const workerZip = String(worker.zipCode || worker.zip || "").trim();
      const workerCity = String(worker.city || "").trim().toLowerCase();
      const workerState = String(worker.state || "TX").trim().toUpperCase();

      if (facilityZip && workerZip && facilityZip === workerZip) score += 30;
      else if (facilityCity && workerCity && facilityCity === workerCity) score += 20;
      else if (facilityState && workerState && facilityState === workerState) score += 10;

      const completedCount = Number(worker.completedShiftCount || worker.completedShifts || 0);
      const noShowCount = Number(worker.noShowCount || worker.noShows || 0);
      const cancelCount = Number(worker.cancelCount || worker.cancellations || 0);

      score += Math.min(completedCount, 20);
      score -= noShowCount * 10;
      score -= cancelCount * 5;

      const assignedToday = shiftsActive.filter((s) => {
        const assignedUid = getAssignedWorkerUid(s);
        if (assignedUid !== worker.uid) return false;

        const st = toMillis(s.startTime);
        if (!Number.isFinite(st)) return false;

        return ymd(startOfDayMs(st)) === ymd(startOfDayMs(startDate.getTime()));
      }).length;

      score -= assignedToday * 8;

      return score;
    }

    const ranked = workerPool
      .filter((w) => {
       const compliance = getWorkerCompliance(w);

const role = String(w.platformRole || w.role || "").toLowerCase();
const status = String(w.status || "active").toLowerCase();
const onboardingStatus = String(w.onboardingStatus || "").toLowerCase();
const workerStatus = String(w.workerStatus || "").toLowerCase();
const workerOperationalState = String(w.workerOperationalState || "").toUpperCase();
const complianceStatus = String(w.complianceStatus || "").toLowerCase();

const adminWorkforceReady =
  role === "worker" &&
  status === "active" &&
  (
    compliance.canWork ||
    onboardingStatus === "approved" ||
    workerStatus === "ready" ||
    workerOperationalState === "READY" ||
    complianceStatus === "approved" ||
    complianceStatus === "ready" ||
    w.onboardingCompleted === true
  );

if (!adminWorkforceReady) return false;
        const validTypes = Array.isArray(w._validTypes) ? w._validTypes : [];

const fallbackApprovedClinical =
  w?.credentialMap?.professional_license?.status === "approved";

if (!validTypes.includes(role) && !fallbackApprovedClinical) return false;

     const inactive = String(w.status || "active").toLowerCase() !== "active";
if (inactive) return false;

if (isWorkerAvailabilityBlocked(w)) return false;

return !workerHasConflict(w.uid, startDate, endDate, shiftsActive, selectedShift.id);

      })

.map((w) => {
  const smart = buildSmartMatch(w, selectedShift);

  return {
    ...w,
    _smartMatch: smart.score,
    _smartReasons: smart.reasons,
    _autoScore: scoreWorker(w),
    _reliabilityScore: calculateReliabilityScore(w, shiftsActive),
  };
})

.sort((a, b) => b._autoScore - a._autoScore);

console.table(
  ranked.map((w) => ({
    email: w.email,
    role: w.primaryRole || w.workerType || w.role,
    reliability: w._reliabilityScore,
    autoScore: w._autoScore,
  }))
);

    if (!ranked.length) {
      alert("No available workers match this shift.");
      return;
    }

    const best = ranked[0];

    setRecommendedWorker(best);

    try {
      await reassignWorkerOnShift(selectedShift.id, best.uid, "auto_assign_priority_score");

      setEditWorkerUid(best.uid);

      alert(`Auto-assigned to ${best.email || best.uid} — score ${best._autoScore}`);
    } catch (err) {
      console.error("auto assign error:", err);
      alert(err?.message || "Auto-assign failed.");
    }
  };

 const handleDeleteSelectedShift = async () => {
  if (!selectedShift) {
    alert(
      "Select a shift first."
    );

    return;
  }

  if (
    !canDeleteShift(
      selectedShift
    )
  ) {
    alert(
      "This shift cannot be deleted."
    );

    return;
  }

  const ok =
    window.confirm(
      `Delete this shift?\n\n${selectedShift.role}\n${fmtShortRange(
        selectedShift.startTime,
        selectedShift.endTime,
        selectedShift
      )}`
    );

  if (!ok) {
    return;
  }

  try {
    const result =
      await removeShift(
        selectedShift.id,
        auth.currentUser?.uid ||
          "planner",
        "planner_single_remove"
      );

    setSelectedShiftId(
      ""
    );

    setSelectedShiftIds(
      (prev) =>
        prev.filter(
          (id) =>
            id !==
            selectedShift.id
        )
    );

    if (
      result?.action ===
      "deleted"
    ) {
      alert(
        "Shift deleted."
      );

      return;
    }

    if (
      result?.action ===
      "cancelled"
    ) {
      alert(
        "Shift cancelled. History has been preserved."
      );

      return;
    }

    alert(
      "Shift processed successfully."
    );
  } catch (err) {
    console.error(
      "remove shift error:",
      err
    );

    alert(
      err?.message ||
        "Failed to process shift."
    );
  }
};

  
    const handleMarkCompleted = async () => {
      try {
        if (!selectedShift?.id) {
          alert("Select a shift first.");
          return;
        }

        const assignedUid =
          selectedShift.assignedWorkerUid ||
          selectedShift.workerUid ||
          selectedShift.workerId ||
          "";

        if (!assignedUid) {
          alert("Shift has no assigned worker.");
          return;
        }

        await setShiftCompleted(
          selectedShift.id,
          assignedUid,
          auth.currentUser?.uid || "planner"
        );

        alert("Shift marked completed.");
      } catch (err) {
        console.error(err);
        alert(err?.message || "Unable to complete shift.");
      }
    };

    const handleMarkNoShow = async () => {
      try {
        if (!selectedShift?.id) {
          alert("Select a shift first.");
          return;
        }

        const reason =
          window.prompt("Reason for no-show?", "Worker failed to arrive") ||
          "Worker failed to arrive";

        await setShiftNoShow(
          selectedShift.id,
          auth.currentUser?.uid || "planner",
          reason
        );

        alert("Shift marked as no-show.");
      } catch (err) {
        console.error(err);
        alert(err?.message || "Unable to mark no-show.");
      }
    };

const handleBulkRemoveSelected = async () => {
  if (!selectedShiftIds.length) {
    alert(
      "Select at least one shift."
    );

    return;
  }

  const rows =
    shifts.filter((shift) =>
      selectedShiftIds.includes(
        shift.id
      )
    );

  if (!rows.length) {
    return;
  }

  const ok =
    window.confirm(
      `Process ${rows.length} selected shift(s)?\n\nThe Shift Service will determine whether each shift may be deleted, cancelled, or must remain protected as history.`
    );

  if (!ok) {
    return;
  }

  const actorUid =
    auth.currentUser?.uid ||
    "planner";

  let deletedCount = 0;
  let cancelledCount = 0;

  const failed = [];

  for (const shift of rows) {
    try {
      const result =
        await removeShift(
          shift.id,
          actorUid,
          "planner_bulk_remove"
        );

      if (
        result?.action ===
        "deleted"
      ) {
        deletedCount++;
      }

      if (
        result?.action ===
        "cancelled"
      ) {
        cancelledCount++;
      }
    } catch (err) {
      console.error(
        "bulk remove shift error:",
        shift.id,
        err
      );

      failed.push({
        id: shift.id,
        message:
          err?.message ||
          "Unable to process shift",
      });
    }
  }

  const processedCount =
    deletedCount +
    cancelledCount;

  if (processedCount > 0) {
    const failedIds =
      new Set(
        failed.map(
          (item) => item.id
        )
      );

    setSelectedShiftIds(
      rows
        .filter((shift) =>
          failedIds.has(
            shift.id
          )
        )
        .map(
          (shift) =>
            shift.id
        )
    );

    if (
      selectedShiftId &&
      rows.some(
        (shift) =>
          shift.id ===
            selectedShiftId &&
          !failedIds.has(
            shift.id
          )
      )
    ) {
      setSelectedShiftId("");
    }
  }

  if (failed.length === 0) {
    alert(
      `Shift processing complete.\n\nDeleted: ${deletedCount}\nCancelled: ${cancelledCount}`
    );

    return;
  }

  alert(
    `Shift processing complete.\n\nDeleted: ${deletedCount}\nCancelled: ${cancelledCount}\nProtected / Failed: ${failed.length}\n\nProtected historical shifts were not modified.`
  );
};

const handleBulkCloseUnfilled = async () => {
  if (!selectedShiftIds.length) {
    alert(
      "Select one or more shifts first."
    );

    return;
  }

  const selectedRows =
    shifts.filter((shift) =>
      selectedShiftIds.includes(
        shift.id
      )
    );

  if (!selectedRows.length) {
    return;
  }

  const ok =
    window.confirm(
      `Close ${selectedRows.length} selected shift(s) as unfilled?\n\nOnly open or offered shifts with no assigned worker will be closed.`
    );

  if (!ok) {
    return;
  }

  const actorUid =
    auth.currentUser?.uid ||
    "planner";

  let closedCount = 0;

  const failed = [];

  for (const shift of selectedRows) {
    try {
      await closeShiftUnfilled(
        shift.id,
        actorUid,
        "bulk_close_unfilled"
      );

      closedCount++;
    } catch (err) {
      console.error(
        "close unfilled shift error:",
        shift.id,
        err
      );

      failed.push({
        id: shift.id,
        message:
          err?.message ||
          "Unable to close shift",
      });
    }
  }

  if (failed.length === 0) {
    clearShiftSelection();

    alert(
      `${closedCount} shift${
        closedCount === 1
          ? ""
          : "s"
      } closed as unfilled.`
    );

    return;
  }

  const failedIds =
    new Set(
      failed.map(
        (item) => item.id
      )
    );

  setSelectedShiftIds(
    selectedRows
      .filter((shift) =>
        failedIds.has(
          shift.id
        )
      )
      .map(
        (shift) =>
          shift.id
      )
  );

  alert(
    `Close Unfilled complete.\n\nClosed: ${closedCount}\nProtected / Failed: ${failed.length}`
  );
};

  function toggleHiddenRole(rk) {
    setHiddenRoleKeys((prev) => {
      const hidden = prev.includes(rk);
      if (hidden) return prev.filter((x) => x !== rk);
      const next = [...prev, rk];
      const remaining = roleOptions.keys.filter((x) => !next.includes(x));
      if (remaining.length === 0) return prev;
      return next;
    });
  }

  function focusRole(rk) {
    setHiddenRoleKeys(roleOptions.keys.filter((x) => x !== rk));
  }

  function clearHiddenRoles() {
    setHiddenRoleKeys([]);
  }

   function getAssignedUidForShift(shift) {
    return (
      shift?.assignedWorkerUid ||
      (Array.isArray(shift?.assignedWorkerUids) && shift.assignedWorkerUids[0]) ||
      ""
    );
  }

function canMarkShiftCompleted(
  shift
) {
  const operational =
    getShiftOperationalSummary(
      shift
    );

  return (
    !!operational
      .assignedWorkerUid &&
    operational.status ===
      "arrived"
  );
}

  function canCloseUnfilledShift(shift) {
    const status = String(shift?.status || "").toLowerCase();
    const assignedUid = getAssignedUidForShift(shift);

    return !assignedUid && ["open", "offered"].includes(status);
  }


async function handleCloseUnfilledShift(shift) {
  try {
    if (!shift?.id) return;

    const ok = window.confirm(
      `Close this ${String(
        shift.role || "shift"
      )} as unfilled / not worked?`
    );

    if (!ok) return;

await closeShiftUnfilled(
  shift.id,
  auth.currentUser?.uid || "",
  "planner_close_unfilled"
);

    if (
      selectedShiftId ===
      shift.id
    ) {
      setSelectedShiftId("");
    }
  } catch (err) {
    console.error(
      "close unfilled error:",
      err
    );

    window.alert(
      err?.message ||
        "Failed to close selected shift."
    );
  }
}

  const CredentialBadges = ({ worker }) => {
    const fallbackCreds = [];

if (worker?.credentialMap?.professional_license?.status === "approved") {
  fallbackCreds.push({
    type: worker.licenseType || worker.workerType || worker.roleType || "Clinical License",
    valid: true,
  });
}

if (worker?.credentialMap?.government_id?.status === "approved") {
  fallbackCreds.push({
    type: "Government ID",
    valid: true,
  });
}

if (worker?.credentialMap?.tb_test?.status === "approved") {
  fallbackCreds.push({
    type: "TB",
    valid: true,
  });
}

if (worker?.credentialMap?.cpr_bls?.status === "approved") {
  fallbackCreds.push({
    type: "CPR/BLS",
    valid: true,
  });
}

const creds =
  Array.isArray(worker?._allCreds) && worker._allCreds.length > 0
    ? worker._allCreds
    : fallbackCreds;

    if (creds.length === 0) {
      return (
        <span
          style={{
            padding: "2px 7px",
            borderRadius: 999,
            fontSize: 10,
            fontWeight: 800,
            background: "#f8fafc",
            border: "1px solid #e2e8f0",
            color: "#64748b",
          }}
        >
          No credentials
        </span>
      );
    }

    const visible = creds.slice(0, 2);
    const remaining = Math.max(0, creds.length - visible.length);

    return (
      <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
        {visible.map((c) => (
          <span
            key={c.type}
            style={{
              padding: "2px 7px",
              borderRadius: 999,
              fontSize: 10,
              fontWeight: 800,
              background: c.valid ? "#eff6ff" : "#fff1f2",
              border: c.valid ? "1px solid #bfdbfe" : "1px solid #fecdd3",
              color: c.valid ? "#1d4ed8" : "#be123c",
            }}
          >
            {c.type}
          </span>
        ))}

        {remaining > 0 ? (
          <span
            style={{
              padding: "2px 7px",
              borderRadius: 999,
              fontSize: 10,
              fontWeight: 800,
              background: "#f8fafc",
              border: "1px solid #e2e8f0",
              color: "#475569",
            }}
          >
            +{remaining}
          </span>
        ) : null}
      </div>
    );
  };

  function needsTimeSet(shift) {
    const status = String(shift?.status || "").toLowerCase();
    const assignedUid = getAssignedWorkerUid(shift);

    return (
      shift?.isFlexibleTime === true &&
      !!assignedUid &&
      ["assigned", "activated", "tracking", "arrived"].includes(status)
    );
  }

  const TimeRequiredStrip = () => {
    const rows = shiftsActive
      .filter((s) => needsTimeSet(s))
      .sort((a, b) => String(a.role || "").localeCompare(String(b.role || "")));

    if (!rows.length) return null;

    return (
      <div
        style={{
          marginBottom: 10,
          padding: "8px 10px",
          border: "1px solid #facc15",
          borderRadius: 14,
          background: "#fef9c3",
          display: "flex",
          alignItems: "center",
          gap: 10,
          flexWrap: "wrap",
        }}
      >
        <div style={{ minWidth: 170 }}>
          <div style={{ fontWeight: 900, fontSize: 14, color: "#713f12" }}>
            Time required
          </div>
          <div style={{ fontSize: 11, fontWeight: 800, color: "#854d0e", marginTop: 2 }}>
            {rows.length} accepted flexible shift{rows.length === 1 ? "" : "s"} need time
          </div>
        </div>

        <div style={{ display: "flex", gap: 8, overflowX: "auto", flex: 1, minWidth: 0 }}>
          {rows.map((s) => {
            const active = selectedShiftId === s.id;
            const colors = roleColorFor(s.role);
            const assignedUid = getAssignedWorkerUid(s);
            const workerLabel = workersByUid.get(assignedUid)?.email || assignedUid || "Assigned";

            return (
              <button
                key={s.id}
                onClick={() => {
                  setSelectedShiftId(s.id);

                  const preset = SHIFT_PRESETS.find((p) => p.key === setTimePreset) || SHIFT_PRESETS[0];
                  const base = new Date(selectedDayMs);

                  if (preset && !preset.isFlexibleTime) {
                    const start = new Date(base);
                    start.setHours(preset.startHour, 0, 0, 0);

                    const end = new Date(base);
                    if (preset.overnight || preset.endHour <= preset.startHour) {
                      end.setDate(end.getDate() + 1);
                    }
                    end.setHours(preset.endHour, 0, 0, 0);

                    setEditStartTime(toLocalInputValue(start));
                    setEditEndTime(toLocalInputValue(end));
                  }
                }}
                style={{
                  minWidth: 190,
                  maxWidth: 230,
                  flexShrink: 0,
                  borderRadius: 12,
                  border: active ? `2px solid ${colors.color}` : "1px solid #facc15",
                  background: active ? "#fff7ed" : "#fff",
                  padding: "8px 10px",
                  textAlign: "left",
                  cursor: "pointer",
                }}
              >
                <div style={{ fontWeight: 800, fontSize: 11, color: colors.color }}>
                  {String(s.role || "-")} • Flexible
                </div>

                <div style={{ fontSize: 11, fontWeight: 800, color: "#713f12", marginTop: 2 }}>
                  Set time now
                </div>

                <div
                  style={{
                    fontSize: 10,
                    fontWeight: 800,
                    color: "#64748b",
                    marginTop: 2,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {workerLabel}
                </div>
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  const WaitingStrip = () => {
    if (!offeredShifts.length) return null;

    return (
      <div
        style={{
          marginBottom: 10,
          padding: "8px 10px",
          border: "1px solid #d8e1ea",
          borderRadius: 14,
          background: "#ffffff",
          boxShadow: "0 6px 14px rgba(15,23,42,.04)",
          display: "flex",
          alignItems: "center",
          gap: 10,
          flexWrap: "wrap",
        }}
      >
        <div style={{ minWidth: 150 }}>
          <div style={{ fontWeight: 900, fontSize: 14, color: "#0f172a" }}>
            Waiting for acceptance
          </div>
          <div style={{ fontSize: 11, fontWeight: 800, color: "#64748b", marginTop: 2 }}>
            {offeredShifts.length} direct offer{offeredShifts.length === 1 ? "" : "s"}
          </div>
        </div>

        <div style={{ display: "flex", gap: 8, overflowX: "auto", flex: 1, minWidth: 0 }}>
          {offeredShifts.map((s) => {
            const active = selectedShiftId === s.id;
            const colors = roleColorFor(s.role);

            return (
              <button
                key={s.id}
                onClick={() => setSelectedShiftId(s.id)}
                style={{
                  minWidth: 138,
                  maxWidth: 150,
                  flexShrink: 0,
                  borderRadius: 12,
                  border: active ? `2px solid ${colors.color}` : `1px solid ${colors.color}33`,
                  background: active ? "#eff6ff" : "#fff",
                  padding: "7px 9px",
                  textAlign: "left",
                }}
              >
                <div style={{ fontWeight: 900, fontSize: 12, color: colors.color }}>
                  {String(s.role || "-")}
                </div>
                <div style={{ fontSize: 11, fontWeight: 800, color: "#334155", marginTop: 2 }}>
                  {fmtShortRange(s.startTime, s.endTime, s)}
                </div>
              </button>
            );
          })}
        </div>
      </div>
    );
  };
 const SelectedShiftPanel = () => {

    const roleColors = roleColorFor(selectedShift.role);
    const isFlexible = selectedShift?.isFlexibleTime;
    const selectedRoleKey = roleKey(selectedShift.role);
    const selectedStatus = String(selectedShift.status || "").toLowerCase();

const isClosedShift = [
  "completed",
  "no_show",
  "cancelled",
  "canceled",
  "closed_unfilled",
].includes(selectedStatus);

  const workerChoices = workersNormalized.filter((w) =>
  workerCanFillRole(w, selectedRoleKey)
);

    const getPrimaryAction = () => {
      const runPrimaryAction = () => {
        if (primaryAction === "save") return handleSaveShiftTime();
        if (primaryAction === "auto") return handleAutoAssignBest();
        if (primaryAction === "assign") return handleReassignSelectedShift();
      };
      if (!selectedShift) return "none";

      const isFlexible = selectedShift?.isFlexibleTime;
      const hasWorker = !!getAssignedWorkerUid(selectedShift);
      const hasPickedWorker = !!editWorkerUid;

      const selectedStartDate = new Date(toMillis(selectedShift.startTime));
      const selectedEndDate = new Date(toMillis(selectedShift.endTime));

      const startChanged =
        Number.isFinite(selectedStartDate.getTime()) &&
        editStartTime !== (selectedShift.timeZone ? zonedInputValue(selectedStartDate, selectedShift.timeZone) : "");

      const endChanged =
        Number.isFinite(selectedEndDate.getTime()) &&
        editEndTime !== (selectedShift.timeZone ? zonedInputValue(selectedEndDate, selectedShift.timeZone) : "");

      if (isFlexible && (!editStartTime || !editEndTime)) return "save";

      if (!hasWorker && !hasPickedWorker) return "auto";

      if (hasPickedWorker && !hasWorker) return "assign";

      if (startChanged || endChanged) return "save";

      return "none";
    };

const primaryAction =
  getPrimaryAction();

const recommendedAction =
  isClosedShift
    ? "This shift is closed. No operational action is required."
    : primaryAction === "auto"
    ? "No worker is assigned. Auto assign the best available worker."
    : primaryAction === "assign"
    ? "A worker is selected. Assign this worker to the shift."
    : primaryAction === "save"
    ? "Shift time changed. Save the updated shift time."
    : "No immediate action required.";

const runPrimaryAction = () => {
  if (isClosedShift) {
    return;
  }

  if (primaryAction === "save") {
    return handleSaveShiftTime();
  }

  if (primaryAction === "auto") {
    return handleAutoAssignBest();
  }

  if (primaryAction === "assign") {
    return handleReassignSelectedShift();
  }

  return undefined;
};

React.useEffect(() => {
  const onSmartNext = (event) => {
    const eventShiftId = String(
      event?.detail?.shiftId || ""
    );

    const currentShiftId = String(
      selectedShift?.id || ""
    );

    if (
      !eventShiftId ||
      !currentShiftId ||
      eventShiftId !== currentShiftId
    ) {
      return;
    }

    setTimeout(() => {
      runPrimaryAction();
    }, 120);
  };

  window.addEventListener(
    "shiftbridge:smart-next",
    onSmartNext
  );

  return () => {
    window.removeEventListener(
      "shiftbridge:smart-next",
      onSmartNext
    );
  };
}, [
  primaryAction,
  selectedShift?.id,
  editWorkerUid,
  editStartTime,
  editEndTime,
  isClosedShift,
]);

React.useEffect(() => {
  const onKeyDown = (event) => {
    const tag = String(
      document.activeElement?.tagName ||
        ""
    ).toLowerCase();

    if (
      [
        "input",
        "textarea",
        "select",
      ].includes(tag)
    ) {
      return;
    }

    if (
      event.key === "Escape"
    ) {
      event.preventDefault();

      clearSelectedShift();

      return;
    }

    if (isClosedShift) {
      return;
    }

    if (
      event.key === "n" ||
      event.key === "N"
    ) {
      event.preventDefault();

      runPrimaryAction();

      return;
    }

    if (
      event.key === "a" ||
      event.key === "A"
    ) {
      event.preventDefault();

      handleReassignSelectedShift();

      return;
    }

    if (
      event.key === "s" ||
      event.key === "S"
    ) {
      event.preventDefault();

      handleSaveShiftTime();
    }
  };

  window.addEventListener(
    "keydown",
    onKeyDown
  );

  return () => {
    window.removeEventListener(
      "keydown",
      onKeyDown
    );
  };
}, [
  selectedShift,
  primaryAction,
  editWorkerUid,
  editStartTime,
  editEndTime,
  isClosedShift,
]);

return (
  <div className="schedule-selected-content"
    style={{
      marginBottom: 10,
      padding: "8px 10px",
      border:
        "1px solid #d8e1ea",
      borderRadius: 14,
      background: "#ffffff",
      boxShadow:
        "0 6px 14px rgba(15,23,42,.04)",
    }}
  >
    <div
      style={{
        display: "flex",
        justifyContent:
          "flex-end",
        marginBottom: 8,
      }}
    >
      <button
        type="button"
        onClick={() =>
          clearSelectedShift()
        }
        aria-label="Close Shift 360"
        title="Close Shift 360"
        style={{
          width: 34,
          height: 34,
          display: "grid",
          placeItems: "center",
          padding: 0,
          borderRadius: 999,
          border:
            "1px solid #cbd5e1",
          background: "#ffffff",
          color: "#334155",
          cursor: "pointer",
          fontSize: 20,
          fontWeight: 900,
          lineHeight: 1,
          boxShadow:
            "0 4px 10px rgba(15,23,42,.08)",
        }}
      >
        ×
      </button>
    </div>

    <div
      style={{
        display: "flex",
        gap: 6,
        marginBottom: 12,
        paddingBottom: 9,
        borderBottom:
          "1px solid #e2e8f0",
        overflowX: "auto",
      }}
    >
      {[
        ["operations", "Operations"],
        ["overview", "Overview"],
        ["timeline", "Timeline"],
        ["location", "Location"],
      ].map(([key, label]) => (
        <button
          key={key}
          type="button"
          onClick={() => {
            setShift360Section(
              key
            );

            setShowShift360More(
              false
            );
          }}
          style={{
            minHeight: 32,
            padding:
              "6px 10px",
            borderRadius: 9,
            border:
              shift360Section ===
              key
                ? "1px solid #2563eb"
                : "1px solid #cbd5e1",
            background:
              shift360Section ===
              key
                ? "#2563eb"
                : "#ffffff",
            color:
              shift360Section ===
              key
                ? "#ffffff"
                : "#334155",
            cursor: "pointer",
            fontSize: 10,
            fontWeight: 900,
            whiteSpace:
              "nowrap",
          }}
        >
          {label}
        </button>
      ))}

      <button
        type="button"
        onClick={() =>
          setShowShift360More(
            (current) =>
              !current
          )
        }
        style={{
          minHeight: 32,
          padding: "6px 10px",
          borderRadius: 9,
          border: [
            "incidents",
            "communications",
            "audit",
            "ai",
          ].includes(
            shift360Section
          )
            ? "1px solid #2563eb"
            : "1px solid #cbd5e1",
          background: [
            "incidents",
            "communications",
            "audit",
            "ai",
          ].includes(
            shift360Section
          )
            ? "#2563eb"
            : "#ffffff",
          color: [
            "incidents",
            "communications",
            "audit",
            "ai",
          ].includes(
            shift360Section
          )
            ? "#ffffff"
            : "#334155",
          cursor: "pointer",
          fontSize: 10,
          fontWeight: 900,
          whiteSpace: "nowrap",
        }}
      >
        More{" "}
        {showShift360More
          ? "▲"
          : "▼"}
      </button>

      {showShift360More ? (
        <div
          style={{
            width: "100%",
            display: "flex",
            gap: 6,
            flexWrap: "wrap",
            paddingTop: 7,
            marginTop: 3,
            borderTop:
              "1px solid #e2e8f0",
          }}
        >
          {[
            [
              "incidents",
              "Incidents",
            ],
            [
              "communications",
              "Communications",
            ],
            ["audit", "Audit"],
            ["ai", "AI"],
          ]
            .filter(([key]) => {
              if (
                key ===
                "incidents"
              ) {
                return canViewShiftIncidents;
              }

              if (
                key ===
                "communications"
              ) {
                return canViewShiftCommunications;
              }

              return true;
            })
            .map(
              ([key, label]) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => {
                    setShift360Section(
                      key
                    );

                    setShowShift360More(
                      false
                    );
                  }}
                  style={{
                    minHeight: 30,
                    padding:
                      "5px 9px",
                    borderRadius: 9,
                    border:
                      shift360Section ===
                      key
                        ? "1px solid #2563eb"
                        : "1px solid #cbd5e1",
                    background:
                      shift360Section ===
                      key
                        ? "#2563eb"
                        : "#f8fafc",
                    color:
                      shift360Section ===
                      key
                        ? "#ffffff"
                        : "#334155",
                    cursor:
                      "pointer",
                    fontSize: 10,
                    fontWeight: 900,
                    whiteSpace:
                      "nowrap",
                  }}
                >
                  {label}
                </button>
              )
            )}
        </div>
      ) : null}
    </div>

    {shift360Section ===
    "overview" ? (
      <Shift360Overview
        shift={selectedShift}
        worker={
          workersByUid.get(
            getAssignedWorkerUid(
              selectedShift
            )
          ) || null
        }
        facility={
          facilityProfile
        }
        eligibilitySummary={
          selectedShiftEligibilitySummary
        }
        showFinancials={
          false
        }
      />
    ) : null}

    {shift360Section ===
    "timeline" ? (
      <Shift360Timeline
        shift={selectedShift}
      />
    ) : null}

    {shift360Section ===
    "location" ? (
      <Shift360Location
        shift={selectedShift}
        facility={
          facilityProfile
        }
      />
    ) : null}

    {shift360Section ===
      "incidents" &&
    canViewShiftIncidents ? (
      <Shift360Incidents
        shift={selectedShift}
        incidents={
          incidents
        }
      />
    ) : null}

    {shift360Section ===
      "communications" &&
    canViewShiftCommunications ? (
      <Shift360Communications
        shift={selectedShift}
        communications={
          communications
        }
      />
    ) : null}

    {shift360Section ===
    "audit" ? (
      <Shift360Audit
        shift={selectedShift}
      />
    ) : null}

    {shift360Section ===
    "ai" ? (
      <Shift360AIInsights
        shift={selectedShift}
        worker={
          workersByUid.get(
            getAssignedWorkerUid(
              selectedShift
            )
          ) || null
        }
        facility={
          facilityProfile
        }
      />
    ) : null}

    <div
      style={{
        display:
          shift360Section ===
          "operations"
            ? "grid"
            : "none",
        gridTemplateColumns:
          "minmax(0, 1fr)",
        gap: 10,
        alignItems: "start",
        width: "100%",
        minWidth: 0,
      }}
    >
      <div>
        <div
          style={{
            fontSize: 10,
            fontWeight: 950,
            color: "#64748b",
            textTransform:
              "uppercase",
            letterSpacing: 0.5,
          }}
        >
          Operations Action Center
        </div>

        <div
          style={{
            marginTop: 5,
            fontWeight: 950,
            fontSize: 18,
            color:
              roleColors.color,
          }}
        >
          {String(
            selectedShift.role ||
              "Shift"
          )}
        </div>

        <div
          style={{
            marginTop: 4,
            fontSize: 12,
            fontWeight: 850,
            color: "#334155",
          }}
        >
          {fmtShortRange(
            selectedShift.startTime,
            selectedShift.endTime,
            selectedShift
          )}
        </div>

        <div
          style={{
            marginTop: 8,
            display:
              "inline-flex",
            alignItems: "center",
            padding:
              "4px 10px",
            borderRadius: 999,
            background: "#f8fafc",
            border:
              "1px solid #dbe4ee",
            color: "#0f172a",
            fontSize: 11,
            fontWeight: 900,
            textTransform:
              "uppercase",
          }}
        >
          {String(
            selectedShift.status ||
              "open"
          )}
        </div>

        {isFlexible ? (
          <div
            style={{
              marginTop: 8,
              fontSize: 11,
              fontWeight: 900,
              color: "#92400e",
              background:
                "#fef3c7",
              padding:
                "5px 9px",
              borderRadius: 999,
              display:
                "inline-block",
            }}
          >
            Flexible — Time not set
          </div>
        ) : null}
      </div>

      <SelectedShiftAssistant
        passesHardGuards={worker => passesRecommendationHardGuards(worker, selectedShift, {
          tenantId, facilityId, roster: facilityWorkers, results: selectedShiftEligibilityResults,
        })}
        facility={facilityProfile}
        selectedShift={
          selectedShift
        }
        workers={
          workersNormalized
        }
        shifts={
          shiftsActive
        }
        onAssign={async (
          worker
        ) => {
          try {
console.log(
  "ASSIGN WORKER SCOPE AUDIT JSON",
  JSON.stringify(
    {
      uid:
        worker?.uid ||
        worker?.id ||
        "",

      email:
        worker?.email ||
        "",

      role:
        worker?.role ||
        "",

      platformRole:
        worker?.platformRole ||
        "",

      tenantId:
        worker?.tenantId ??
        null,

      facilityId:
        worker?.facilityId ??
        null,

      primaryFacilityId:
        worker?.primaryFacilityId ??
        null,

      facilityIds:
        Array.isArray(
          worker?.facilityIds
        )
          ? worker.facilityIds
          : [],

      allowedFacilityIds:
        Array.isArray(
          worker?.allowedFacilityIds
        )
          ? worker.allowedFacilityIds
          : [],

      assignedFacilityIds:
        Array.isArray(
          worker?.assignedFacilityIds
        )
          ? worker.assignedFacilityIds
          : [],

      plannerTenantId:
        tenantId,

      plannerFacilityId:
        facilityId,

      plannerAllowedFacilityIds:
        allowedFacilityIds,
    },
    null,
    2
  )
);
            if (
              !worker?.uid ||
              !selectedShift?.id
            ) {
              return;
            }

            await reassignWorkerOnShift(
              selectedShift.id,
              worker.uid,
              "ai_assignment_center"
            );

            setEditWorkerUid(
              worker.uid
            );

            alert(
              `${
                worker.email ||
                worker.uid
              } assigned.`
            );
          } catch (err) {
            console.error(
              "assistant assign error:",
              err
            );

            alert(
              err?.message ||
                "Unable to assign worker. This worker may already have an overlapping shift."
            );
          }
        }}
      />
      <div
        style={{
          display: "grid",
          gap: 10,
          width: "100%",
          minWidth: 0,
        }}
      >
        <div
          style={{
            width: "100%",
            boxSizing: "border-box",
            padding: "10px 12px",
            borderRadius: 14,

            background:
              isClosedShift
                ? "#f8fafc"
                : primaryAction === "none"
                ? "#f8fafc"
                : "#eff6ff",

            border:
              isClosedShift
                ? "1px solid #e2e8f0"
                : primaryAction === "none"
                ? "1px solid #e2e8f0"
                : "1px solid #bfdbfe",

            color:
              isClosedShift
                ? "#475569"
                : primaryAction === "none"
                ? "#475569"
                : "#1d4ed8",

            fontSize: 12,
            fontWeight: 900,
            lineHeight: 1.45,
          }}
        >
          Recommended Action:{" "}
          {recommendedAction}
        </div>

        <button
          type="button"
          className="btn btnPrimary"
          onClick={runPrimaryAction}
          disabled={
            isClosedShift ||
            primaryAction === "none"
          }
          style={{
            opacity:
              isClosedShift ||
              primaryAction === "none"
                ? 0.5
                : 1,

            width: "100%",
            justifyContent: "center",
            fontWeight: 950,
            borderRadius: 12,
            padding: "10px 14px",

            cursor:
              isClosedShift ||
              primaryAction === "none"
                ? "not-allowed"
                : "pointer",

            boxShadow:
              isClosedShift ||
              primaryAction === "none"
                ? "none"
                : "0 10px 22px rgba(37,99,235,.22)",
          }}
        >
          {isClosedShift
            ? "Shift Closed"
            : "Do Next"}
        </button>

        <details
          style={{
            width: "100%",
            boxSizing: "border-box",
            borderRadius: 12,
            border:
              "1px solid #cbd5e1",
            background: "#ffffff",
            overflow: "hidden",
          }}
        >
          <summary
            style={{
              padding: "10px 12px",
              color: "#334155",
              fontSize: 12,
              fontWeight: 900,
              cursor: "pointer",
              userSelect: "none",
            }}
          >
            Assignment Options
          </summary>

          <div
            style={{
              display: "grid",
              gap: 8,
              padding:
                "2px 12px 12px",
            }}
          >
            {isClosedShift ? (
              <div
                style={{
                  padding: 10,
                  borderRadius: 10,
                  background:
                    "#f8fafc",
                  border:
                    "1px solid #e2e8f0",
                  color: "#64748b",
                  fontSize: 11,
                  fontWeight: 800,
                  lineHeight: 1.45,
                }}
              >
                Assignment is unavailable because
                this shift is closed.
              </div>
            ) : null}

            <button
              type="button"
              className={`btn ${
                primaryAction === "auto"
                  ? "btnPrimary"
                  : ""
              }`}
              onClick={
                handleAutoAssignBest
              }
              disabled={
                isClosedShift
              }
              style={{
                width: "100%",
                justifyContent:
                  "center",

                opacity:
                  isClosedShift
                    ? 0.5
                    : 1,

                cursor:
                  isClosedShift
                    ? "not-allowed"
                    : "pointer",

                boxShadow:
                  !isClosedShift &&
                  primaryAction === "auto"
                    ? "0 0 0 2px rgba(37,99,235,.2)"
                    : "none",
              }}
            >
              Auto Assign
            </button>

            <button
              type="button"
              className={`btn ${
                primaryAction === "assign"
                  ? "btnPrimary"
                  : ""
              }`}
              onClick={
                handleReassignSelectedShift
              }
              disabled={
                isClosedShift
              }
              style={{
                width: "100%",
                justifyContent:
                  "center",

                opacity:
                  isClosedShift
                    ? 0.5
                    : 1,

                cursor:
                  isClosedShift
                    ? "not-allowed"
                    : "pointer",

                boxShadow:
                  !isClosedShift &&
                  primaryAction === "assign"
                    ? "0 0 0 2px rgba(37,99,235,.2)"
                    : "none",
              }}
            >
              Manual Assign
            </button>
          </div>
        </details>

        <details
          style={{
            width: "100%",
            boxSizing: "border-box",
            borderRadius: 12,
            border:
              "1px solid #cbd5e1",
            background: "#ffffff",
            overflow: "hidden",
          }}
        >
          <summary
            style={{
              padding: "10px 12px",
              color: "#334155",
              fontSize: 12,
              fontWeight: 900,
              cursor: "pointer",
              userSelect: "none",
            }}
          >
            Shift Actions
          </summary>

          <div
            style={{
              display: "grid",
              gap: 8,
              padding:
                "2px 12px 12px",
            }}
          >
            <button
              type="button"
              className={`btn ${
                primaryAction === "save"
                  ? "btnPrimary"
                  : ""
              }`}
              onClick={
                handleSaveShiftTime
              }
              disabled={
                isClosedShift
              }
              style={{
                width: "100%",
                justifyContent:
                  "center",

                opacity:
                  isClosedShift
                    ? 0.5
                    : 1,

                cursor:
                  isClosedShift
                    ? "not-allowed"
                    : "pointer",

                boxShadow:
                  !isClosedShift &&
                  primaryAction === "save"
                    ? "0 0 0 2px rgba(37,99,235,.2)"
                    : "none",
              }}
            >
              {isFlexible
                ? "Set Shift Time"
                : "Save Shift"}
            </button>

            {!isClosedShift &&
            [
              "assigned",
              "activated",
              "tracking",
            ].includes(
              selectedStatus
            ) ? (
              <button
                type="button"
                className="btn"
                onClick={
                  handleMarkNoShow
                }
                style={{
                  width: "100%",
                  justifyContent:
                    "center",
                  color: "#b91c1c",
                  borderColor:
                    "#fecaca",
                  background:
                    "#fee2e2",
                  fontWeight: 900,
                }}
              >
                Mark No Show
              </button>
            ) : null}

            <button
              type="button"
              className="btn"
              onClick={
                handleDeleteSelectedShift
              }
              disabled={
                isClosedShift
              }
              style={{
                width: "100%",
                justifyContent:
                  "center",

                color:
                  isClosedShift
                    ? "#94a3b8"
                    : "#b91c1c",

                borderColor:
                  isClosedShift
                    ? "#e2e8f0"
                    : "#fecaca",

                background:
                  isClosedShift
                    ? "#f8fafc"
                    : "#fff7f7",

                fontWeight: 900,

                opacity:
                  isClosedShift
                    ? 0.6
                    : 1,

                cursor:
                  isClosedShift
                    ? "not-allowed"
                    : "pointer",
              }}
            >
              {isClosedShift
                ? "Closed Shift"
                : "Delete / Cancel Shift"}
            </button>
          </div>
        </details>
      </div>
    </div>
  </div>
);
};
  const RoleVisibilityStrip = () => (
    <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 0 }}>
      <button className="btn" onClick={clearHiddenRoles}>
        Show all
      </button>

      {roleOptions.keys.map((rk) => {
        const label = roleOptions.map.get(rk) || rk;
        const hidden = hiddenRoleKeys.includes(rk);
        const colors = roleColorFor(label);

        return (
          <div key={rk} style={{ display: "flex", gap: 4 }}>
            <button
              className="btn"
              onClick={() => toggleHiddenRole(rk)}
              style={{
                borderRadius: 999,
                padding: "6px 10px",
                fontSize: 11,
                fontWeight: 800,
                opacity: hidden ? 0.5 : 1,
                border: `1px solid ${colors.color}55`,
                background: hidden ? "#fff" : `${colors.color}14`,
                color: colors.color,
                boxShadow: hidden ? "none" : `inset 0 0 0 1px ${colors.color}18`,
              }}
            >
              {hidden ? `Show ${label}` : `Hide ${label}`}
            </button>

            {!hidden ? (
              <button className="btn" onClick={() => focusRole(rk)}>
                Focus
              </button>
            ) : null}
          </div>
        );
      })}
    </div>
  );

  const SelectionActionBar = () => {
    if (!selectedShiftIds.length) return null;

    return (
      <div
        style={{
          ...cardStyle(),
          marginBottom: 10,
          padding: "8px 10px",
          display: "flex",
          justifyContent: "space-between",
          gap: 10,
          flexWrap: "wrap",
          alignItems: "center",
          background: "#fff7ed",
          border: "1px solid #fdba74",
        }}
      >
        <div style={{ fontWeight: 900, color: "#0f172a", fontSize: 13 }}>
          {selectedShiftIds.length} selected
        </div>

        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button className="btn" onClick={clearShiftSelection}>
            Clear Selection
          </button>

          <button
            className="btn"
            onClick={handleBulkCloseUnfilled}
            style={{
              color: "#9a3412",
              borderColor: "#fdba74",
              background: "#fff7ed",
              fontWeight: 900,
            }}
          >
            Close Selected
          </button>

          <button
            className="btn"
            style={{
              color: "#b91c1c",
              borderColor: "#fecaca",
              background: "#fff1f2",
              fontWeight: 900,
            }}
            onClick={handleBulkRemoveSelected}
          >
            Delete / Cancel
          </button>
        </div>
      </div>
    );
  };

  
  const CompletedTodayPanel = () => {
    const completedRows = selectDailyShifts(shiftsActive, selectedDayMs, facilityTimeZone)
      .filter(isActiveCompletedShift)
      .sort((a,b) => toMillis(a.startTime)-toMillis(b.startTime));

    if (!completedRows.length) return null;

    return (
      <div style={{ ...cardStyle(), marginTop: 12, marginBottom: 24, overflow: "hidden" }}>
        <button
          type="button"
          onClick={() => setCompletedPanelOpen((v) => !v)}
          style={{
            width: "100%",
            padding: "12px 14px",
            border: "none",
            borderBottom: completedPanelOpen ? "1px solid #e9eef5" : "none",
            background: "#ffffff",
            cursor: "pointer",
            textAlign: "left",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 10,
          }}
        >
          <div>
            <div style={{ fontWeight: 900, fontSize: 15, color: "#166534" }}>
              Completed Today
            </div>
            <div className="smallMeta" style={{ marginTop: 3 }}>
              {completedRows.length} completed shift{completedRows.length === 1 ? "" : "s"} for selected day
            </div>
          </div>

          <div
            style={{
              fontSize: 12,
              fontWeight: 900,
              color: "#166534",
              background: "#dcfce7",
              border: "1px solid #86efac",
              borderRadius: 999,
              padding: "5px 10px",
            }}
          >
            {completedPanelOpen ? "Hide ▲" : "Show ▼"}
          </div>
        </button>

        {completedPanelOpen ? (
          <div style={{ padding: 10, display: "grid", gap: 8 }}>
            {completedRows.map((shift) => {
              const assignedUid = getAssignedWorkerUid(shift);
              const workerLabel = workersByUid.get(assignedUid)?.email || assignedUid || "—";

              return (
                <div
                  key={shift.id}
                  style={{
                    display: "grid",
                    gridTemplateColumns: isMobile ? "1fr" : "1.2fr .7fr .9fr .7fr",
                    gap: 8,
                    alignItems: "center",
                    padding: "8px 10px",
                    borderRadius: 12,
                    background: "#f8fafc",
                    border: "1px solid #dbe4ee",
                  }}
                >
                  <div style={{ fontWeight: 900, fontSize: 12, color: "#0f172a" }}>
                    {workerLabel}
                  </div>

                  <div style={{ fontWeight: 900, fontSize: 12, color: roleColorFor(shift.role).color }}>
                    {String(shift.role || "—")}
                  </div>

                  <div style={{ fontWeight: 800, fontSize: 12, color: "#334155" }}>
                    {fmtShortRange(shift.startTime, shift.endTime, shift)}
                  </div>

                  <div
                    style={{
                      justifySelf: isMobile ? "start" : "end",
                      padding: "4px 9px",
                      borderRadius: 999,
                      background: "#dcfce7",
                      border: "1px solid #86efac",
                      color: "#166534",
                      fontSize: 11,
                      fontWeight: 900,
                    }}
                  >
                    Completed
                  </div>
                </div>
              );
            })}
          </div>
        ) : null}
      </div>
    );
  };
  const PastShiftsPanel = () => {
    if (!pastShiftsForSelectedDay.length) return null;

    return (
      <div
        style={{
          ...cardStyle(),
          marginTop: 12,
          marginBottom: 24,
          overflow: "hidden",
        }}
      >
        <button
          type="button"
          onClick={() => setPastPanelOpen((v) => !v)}
          style={{
            width: "100%",
            padding: "12px 14px",
            border: "none",
            borderBottom: pastPanelOpen ? "1px solid #e9eef5" : "none",
            background: "#ffffff",
            cursor: "pointer",
            textAlign: "left",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 10,
          }}
        >
          <div>
            <div style={{ fontWeight: 900, fontSize: 15, color: "#0f172a" }}>
              Past shifts —{" "}
              {new Date(selectedDayMs).toLocaleDateString(undefined, {
                weekday: "long",
                month: "short",
                day: "numeric",
              })}
            </div>

            <div className="smallMeta" style={{ marginTop: 3 }}>
              {pastShiftsForSelectedDay.length} ended shift
              {pastShiftsForSelectedDay.length === 1 ? "" : "s"}
            </div>
          </div>

          <div
            style={{
              fontSize: 12,
              fontWeight: 900,
              color: "#334155",
              background: "#f8fafc",
              border: "1px solid #dbe4ee",
              borderRadius: 999,
              padding: "5px 10px",
            }}
          >
            {pastPanelOpen ? "Hide ▲" : "Show ▼"}
          </div>
        </button>

        {pastPanelOpen ? (
          <div
            style={{
              padding: 10,
              overflowX: "auto",
              overflowY: "visible",
            }}
          >
            <SelectionActionBar />

            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                marginBottom: 10,
                gap: 8,
              }}
            >
              <button
                className="btn"
                onClick={() => setSelectedShiftIds(pastShiftsForSelectedDay.map((s) => s.id))}
              >
                Select All
              </button>
            </div>
            <SelectionActionBar />

            <button className="btn" onClick={clearShiftSelection}>
              Deselect All
            </button>

            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 10,
                flexWrap: "wrap",
                gap: 8,
              }}
            >
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 800,
                  color: "#475569",
                }}
              >
                Select multiple shifts then choose an action.
              </div>
              <button
                className="btn"
                onClick={() =>
                  setSelectedShiftIds(
                    pastShiftsForSelectedDay.map((s) => s.id)
                  )
                }
              >
                Select All
              </button>

              <button
                className="btn"
                onClick={clearShiftSelection}
              >
                Deselect All
              </button>
            </div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: isMobile
                  ? "1fr"
                  : isTablet
                    ? "repeat(2, minmax(0,1fr))"
                    : "repeat(3, minmax(0,1fr))",
                gap: 8,
              }}
            >
              {pastShiftsForSelectedDay.map((shift) => {
                const showComplete = canMarkShiftCompleted(shift);
                const showCloseUnfilled = canCloseUnfilledShift(shift);

                return (
                  <div
                    key={shift.id}
                    style={{
                      display: "grid",
                      gap: 4,
                      alignContent: "start",
                    }}
                  >
                    <PlannerShiftCard
                      shift={shift}
                      compactHeight={76}
                                          
                      LifecycleBoxes={LifecycleBoxes}
                    />

                    {showComplete || showCloseUnfilled ? (
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "flex-end",
                          gap: 6,
                          flexWrap: "wrap",
                        }}
                      >
                        {showCloseUnfilled ? (
                          <button
                            className="btn"
                            onClick={() => handleCloseUnfilledShift(shift)}
                            style={{
                              borderRadius: 999,
                              padding: "4px 9px",
                              minHeight: 0,
                              fontSize: 11,
                              fontWeight: 900,
                              background: "#fff7ed",
                              border: "1px solid #fdba74",
                              color: "#9a3412",
                              cursor: "pointer",
                            }}
                          >
                            Close Selected
                          </button>
                        ) : null}

                        {showComplete ? (
                          <button
                            className="btn"
                            onClick={() => {
                              setSelectedShiftId(shift.id);
                              handleMarkCompleted();
                            }}
                            style={{
                              borderRadius: 999,
                              padding: "4px 9px",
                              minHeight: 0,
                              fontSize: 11,
                              fontWeight: 900,
                              background: "#dcfce7",
                              border: "1px solid #86efac",
                              color: "#166534",
                              cursor: "pointer",
                            }}
                          >
                            Complete
                          </button>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          </div>
        ) : null}
      </div>
    );
  };

  const todayKpis = useMemo(() => {
    const rows = shiftsForSelectedDay || [];

    const open = rows.filter((s) =>
      ["open", "offered"].includes(String(s.status || "").toLowerCase())
    ).length;

    const assigned = rows.filter((s) =>
      ["assigned", "activated", "tracking", "arrived"].includes(String(s.status || "").toLowerCase())
    ).length;

    const completed = rows.filter(
      (s) => String(s.status || "").toLowerCase() === "completed"
    ).length;

    const worked = assigned + completed;
    const total = open + worked;
    const fillRate = total ? Math.round((worked / total) * 100) : 0;

    const hoursToday = rows
      .filter((s) => ["completed", "arrived", "activated", "assigned"].includes(String(s.status || "").toLowerCase()))
      .reduce((sum, s) => {
        const st = toMillis(s.startTime);
        const en = toMillis(s.endTime);
        if (!Number.isFinite(st) || !Number.isFinite(en) || en <= st) return sum;
        return sum + (en - st) / (1000 * 60 * 60);
      }, 0);

    return {
      open,
      assigned,
      completed,
      hoursToday,
      fillRate,
    };
  }, [shiftsForSelectedDay]);


  const isFlexibleCreate = newShiftPreset === "flex";

  const facilityDisplayName = facilityProfile.name || facilityProfile.facilityName || facilityId || "Facility";
  const openSchedule = (focus = null) => {
    if (focus?.role) {
      setHiddenRoleKeys(previous => previous.filter(role => role !== focus.role));
      setMobileBoardRole(focus.role);
    }
    setScheduleFocus(focus); setLifecycleFilter(""); setMode("manual"); setPlannerView("schedule");
  };
  const openDailyShift = shift => {
    setShowClosed(true); openSchedule(); setSelectedShiftId(shift.id);
  };
  const [scheduleStaffRequest, setScheduleStaffRequest] = useState(0);
  const openStaff = role => {
    setScheduleStaffRequest(request => request + 1);
    openSchedule(); setStaffPoolTab("all"); setSearchTerm(role ? role.replace(/_/g, " ") : "");
    setMatchSelectedShiftRole(false);
    requestAnimationFrame(() => document.getElementById("planner-staff-pool")?.scrollIntoView({ behavior: "smooth", block: "start" }));
  };
  const navigatePlanner = key => {
    if (key === "daily") { setPlannerView("daily"); setSelectedShiftId(null); }
    else if (key === "staff") openStaff();
    else if (key === "schedule" || key === "assignments") openSchedule();
    else setWorkspacePanel(key);
  };
 const changePlannerDate = value => {
  const day = startOfDayMs(value);

  setDateScope("custom");
  setSelectedDayMs(day);
  setCalendarAnchorMs(day);
  setCalendarExpanded(false);
  setSelectedShiftId(null);
};
return (
  <DepartmentLayout planner embedded={embedded}
    navigation={<PlannerNavigation embedded={embedded} active={plannerView} onNavigate={navigatePlanner} />}
    header={<PlannerWorkspaceHeader facilityName={facilityDisplayName} selectedDayMs={selectedDayMs} onDateChange={changePlannerDate}
      onCreate={() => { setMode("manual"); setCreatePanelOpen(true); }} userName={profile?.displayName || profile?.name || plannerEmail} onSignOut={() => signOut(auth)} />}
  >
      <div
        style={{
          maxWidth: 1380,
          margin: "0 auto",
          padding: isMobile ? 10 : 14,
        }}
      >
        <OverflowPanelModal
          overflowPanel={overflowPanel}
          overflowTitle={overflowTitle}
          setOverflowPanel={setOverflowPanel}
          setOverflowTitle={setOverflowTitle}
          PlannerShiftCard={PlannerShiftCard}
          now={now}
          selectedShiftId={selectedShiftId}
          selectedShiftIds={selectedShiftIds}
          toggleShiftSelection={toggleShiftSelection}
          setSelectedShiftId={setSelectedShiftId}
          workersByUid={workersByUid}
          shortWorkerLabel={shortWorkerLabel}
          LifecycleBoxes={LifecycleBoxes}
        />

{createPanelOpen && <PlannerDialog title="Create Shift" onClose={() => setCreatePanelOpen(false)} wide>
<SourceModeTabs mode={mode} setMode={setMode} />
{mode === "manual" ? <PlannerCreateShiftPanel DEFAULT_ROLES={DEFAULT_ROLES}
  SHIFT_PRESETS={SHIFT_PRESETS}
  addDays={addDays}
  allowedRoles={allowedRoles}
  applyPreset={applyPreset}
  calendarAnchorMs={calendarAnchorMs}
  calendarExpanded={calendarExpanded}
  cardStyle={cardStyle}
  createQty={createQty}
  handleCreateShift={handleCreateShift}
  isFlexibleCreate={isFlexibleCreate}
  isMobile={isMobile}
  isTablet={isTablet}
  newShiftEnd={newShiftEnd}
  newShiftMode={newShiftMode}
  newShiftPreset={newShiftPreset}
  newShiftRole={newShiftRole}
  newShiftStart={newShiftStart}
  roleColorFor={roleColorFor}
  schedulerConflictSummary={schedulerConflictSummary}
  selectedDayMs={selectedDayMs}
  selectedWorkerUid={selectedWorkerUid}
  setCalendarAnchorMs={setCalendarAnchorMs}
  setCalendarExpanded={setCalendarExpanded}
  setCreateQty={setCreateQty}
  setDateScope={setDateScope}
  setNewShiftEnd={setNewShiftEnd}
  setNewShiftMode={setNewShiftMode}
  setNewShiftRole={setNewShiftRole}
  setNewShiftStart={setNewShiftStart}
  setSelectedDayMs={setSelectedDayMs}
  setSelectedDirectWorkerUids={setSelectedDirectWorkerUids}
  setSelectedWorkerUid={setSelectedWorkerUid}
  startOfDayMs={startOfDayMs}
  visibleCalendarDays={visibleCalendarDays}
  workerPool={workerPool}
  ymd={ymd} /> : <p>{mode === "import" ? "Import schedule UI coming next." : "API integration coming next."}</p>}
<details><summary>Shift posting help</summary><ShiftBridgeAssist title="Shift Posting" steps={["Choose facility, role, date and time.", "Publish to the Pool or make a Direct Offer.", "Review requests and worker acceptance before monitoring activation, En Route and arrival."]} /></details>
</PlannerDialog>}
{workspacePanel && <PlannerDialog title={workspacePanel === "settings" ? "Planner Settings" : workspacePanel === "reports" ? "Operational Report" : "Shift Messages"} onClose={() => setWorkspacePanel(null)} wide>
  {workspacePanel === "settings" ? <><p>{facilityDisplayName}</p><p>Authorized facility: {facilityId}</p><p>Roles: {roleOptions.keys.join(", ")}</p><p>Facility timezone: {facilityTimeZone || 'Unknown — configure a facility or tenant timezone before scheduling'}.</p><SourceModeTabs mode={mode} setMode={setMode} /><p>{mode === "manual" ? "Manual scheduling is available in Create Shift." : mode === "import" ? "Import schedule UI coming next." : "API integration coming next."}</p></> : workspacePanel === "reports" ? <><p>{new Date(selectedDayMs).toLocaleDateString()} · {facilityDisplayName}</p><p>{dailyModel.metrics.covered} covered / {dailyModel.metrics.total} posted positions. {dailyModel.metrics.completed} completed.</p><p>Coverage requires worker acceptance or a subsequent canonical lifecycle stage. Cancelled shifts are excluded from posted demand.</p></> : <><p>Communications remain attached to their shift. Choose a shift to open its permission-controlled communications.</p><div className="daily-summary-list">{dailyModel.rows.map(row => <button key={row.shift.id} onClick={() => { setWorkspacePanel(null); openDailyShift(row.shift); setShift360Section("communications"); }}>{row.role} · {formatShiftInstant(getShiftStartMs(row.shift), row.shift.timeZone, {dateStyle:"short", timeStyle:"short"})}</button>)}</div>{!dailyModel.rows.length && <p>No shifts for this date.</p>}</>}
</PlannerDialog>}
{plannerView === "daily" ? <PlannerDailyBoard key={tenantId + facilityId} model={dailyModel} facilityName={facilityDisplayName} selectedDayMs={selectedDayMs} now={now}
  workersByUid={workersByUid} availableWorkers={dailyAvailable.workers} availableRoles={dailyAvailable.roles}
  onOpenShift={openDailyShift} onOpenSchedule={openSchedule} onOpenStaff={openStaff}
  loading={shiftsLoading || facilityWorkersLoading} error={shiftsDataError || facilityWorkersDataError || facilityProfileDataError} /> : <div className="planner-schedule">
<div className="schedule-title"><h1>Schedule</h1><p>{new Date(selectedDayMs).toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", year: "numeric" })}</p></div>
{scheduleFocus && <div className="daily-filter-banner">Filtered schedule: {scheduleFocus.role || "All roles"} · {dailyModel.windows.find(w => w.key === scheduleFocus.window)?.label || "All windows"}<button onClick={() => setScheduleFocus(null)}>Clear filter</button></div>}
<PlannerHeader
  auth={auth}
  isMobile={isMobile}
  isTablet={isTablet}
  facilityId={facilityId}
  dateScope={dateScope}
  setDateScope={setDateScope}
  startOfDayMs={startOfDayMs}
  addDays={addDays}
  setSelectedDayMs={setSelectedDayMs}
  setCalendarAnchorMs={setCalendarAnchorMs}
  setCalendarExpanded={setCalendarExpanded}
  showClosed={showClosed}
  setShowClosed={setShowClosed}
  mode={mode}
  setMode={setMode}
  SourceModeTabs={SourceModeTabs}
  operationalControls={<AutoNoShowControl autoNoShowEnabled={autoNoShowEnabled} setAutoNoShowEnabled={setAutoNoShowEnabled} />}
/>

        {error ? (
          <div
            style={{
              ...cardStyle(),
              prginBottom: 12,
              color: "#b91c1c",
              fontWeight: 800,
              background: "#fff5f5",
              border: "1px solid #fecaca",
            }}
          >
            {error}
          </div>
        ) : null}


        {mode === "manual" ? (
          <>
            

            <ScheduleWorkspace isMobile={isMobile} isTablet={isTablet}
              staffPoolRequest={scheduleStaffRequest} selectedShiftId={selectedShiftId}
              onCloseSelectedShift={clearSelectedShift}
              selectedShiftPanel={selectedShift ? <SelectedShiftPanel /> : null}
              staffPool={<>
                  {pendingRequests.filter(({ shift }) => {
                    const status = String(shift?.status || "").toLowerCase();
                    const assignedUid = getAssignedWorkerUid(shift);
                    const req = Array.isArray(shift?.requestedWorkerUids)
                      ? shift.requestedWorkerUids.filter(Boolean)
                      : [];

                    return status === "open" && !assignedUid && req.length > 0;
                  }).length > 0 ? (
                    <div
                      style={{
                        border: "1px solid #bfdbfe",
                        borderRadius: 14,
                        padding: 10,
                        background: "#eff6ff",
                        marginBottom: 12,
                        boxShadow: "0 6px 16px rgba(37,99,235,.08)",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          gap: 8,
                          marginBottom: 8,
                        }}
                      >
                        <div style={{ fontSize: 13, fontWeight: 950, color: "#1d4ed8" }}>
                          Pending approvals
                        </div>

                        <div
                          style={{
                            fontSize: 11,
                            fontWeight: 900,
                            color: "#1d4ed8",
                            background: "#ffffff",
                            border: "1px solid #bfdbfe",
                            borderRadius: 999,
                            padding: "3px 8px",
                          }}
                        >
                            {pendingRequests.filter(({ shift }) => {
                              const status = String(shift?.status || "").toLowerCase();
                              const assignedUid = getAssignedWorkerUid(shift);
                              const req = Array.isArray(shift?.requestedWorkerUids)
                                ? shift.requestedWorkerUids.filter(Boolean)
                                : [];

                              return status === "open" && !assignedUid && req.length > 0;
                            }).length}
                        </div>
                      </div>

                      <div style={{ display: "grid", gap: 8 }}>
                          {pendingRequests
                            .filter(({ shift }) => {
                              const status = String(shift?.status || "").toLowerCase();
                              const assignedUid = getAssignedWorkerUid(shift);
                              const req = Array.isArray(shift?.requestedWorkerUids)
                                ? shift.requestedWorkerUids.filter(Boolean)
                                : [];

                              return status === "open" && !assignedUid && req.length > 0;
                            })
                           .map(({ shiftId, shift, workerUid }) => {
  const label =
    workersByUid.get(workerUid)?.email ||
    workerUid;

  const roleColor =
    roleColorFor(shift.role).color;

  const shiftStartMs =
    getShiftStartMs(shift);

  const shiftEndMs =
    getShiftEndMs(shift);

  const hasWorkerConflict =
    Number.isFinite(shiftStartMs) &&
    Number.isFinite(shiftEndMs) &&
    shiftEndMs > shiftStartMs
      ? workerHasConflict(
          workerUid,
          new Date(shiftStartMs),
          new Date(shiftEndMs),
          shiftsActive,
          shift.id
        )
      : false;

  return (
                            <div
                              key={`${shiftId}-${workerUid}`}
                              style={{
                                display: "grid",
                                gridTemplateColumns: "1fr auto",
                                gap: 8,
                                alignItems: "center",
                                border: "1px solid #dbeafe",
                                borderRadius: 12,
                                padding: "9px 10px",
                                background: "#ffffff",
                              }}
                            >
                              <div style={{ minWidth: 0 }}>
                                <div
                                  style={{
                                    fontWeight: 950,
                                    fontSize: 12,
                                    color: "#0f172a",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {label}
                                </div>

                                <div
                                  style={{
                                    marginTop: 3,
                                    display: "flex",
                                    gap: 6,
                                    flexWrap: "wrap",
                                    alignItems: "center",
                                  }}
                                >
                                  <span
                                    style={{
                                      fontWeight: 900,
                                      fontSize: 10,
                                      color: roleColor,
                                      background: `${roleColor}12`,
                                      border: `1px solid ${roleColor}33`,
                                      borderRadius: 999,
                                      padding: "3px 7px",
                                    }}
                                  >
                                    {String(shift.role || "—")}
                                  </span>

                                  <span
                                    style={{
                                      fontWeight: 800,
                                      fontSize: 10,
                                      color: "#475569",
                                    }}
                                  >
                                    {fmtShortRange(shift.startTime, shift.endTime, shift)}
                                  </span>
                                </div>
                              </div>

                              <div
  style={{
    display: "flex",
    alignItems: "center",
    gap: 6,
  }}
>
  <button
    type="button"
    className="btn"
    style={{
      padding: "7px 10px",
      fontSize: 11,
      fontWeight: 950,
      borderRadius: 999,
      background: "#ffffff",
      color: "#b91c1c",
      border: "1px solid #fecaca",
      cursor: "pointer",
    }}
    onClick={() =>
      handleDeclineRequest(workerUid, shiftId)
    }
  >
    Decline
  </button>

  {hasWorkerConflict ? (
    <span
      title="Worker already has an overlapping assigned shift"
      style={{
        padding: "7px 10px",
        fontSize: 11,
        fontWeight: 950,
        borderRadius: 999,
        background: "#fff7ed",
        color: "#c2410c",
        border: "1px solid #fed7aa",
        whiteSpace: "nowrap",
      }}
    >
      Overlap conflict
    </span>
  ) : (
    <button
      type="button"
      className="btn btnPrimary"
      style={{
        padding: "7px 11px",
        fontSize: 11,
        fontWeight: 950,
        borderRadius: 999,
        background: "#2563eb",
        color: "#ffffff",
        border: "none",
        boxShadow:
          "0 8px 18px rgba(37,99,235,.22)",
        cursor: "pointer",
      }}
      onClick={() =>
        handleApproveRequest(
          workerUid,
          shiftId
        )
      }
    >
      Approve
    </button>
  )}
</div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ) : null}
<StaffPoolPanel
  workerPool={workerPool}
  searchTerm={searchTerm}
  setSearchTerm={setSearchTerm}
  staffPoolTab={staffPoolTab}
  setStaffPoolTab={setStaffPoolTab}
  poolFilter={poolFilter}
  showDisabledWorkers={showDisabledWorkers}
  setShowDisabledWorkers={setShowDisabledWorkers}
  matchSelectedShiftRole={matchSelectedShiftRole}
  setMatchSelectedShiftRole={setMatchSelectedShiftRole}
  editWorkerUid={editWorkerUid}
  setEditWorkerUid={setEditWorkerUid}
  expandedWorkerUid={expandedWorkerUid}
  setExpandedWorkerUid={setExpandedWorkerUid}
  selectedShiftId={selectedShiftId}
  availabilityStatusMeta={availabilityStatusMeta}
  calculateReliabilityScore={calculateReliabilityScore}
  shiftsActive={shiftsActive}
  CredentialBadges={CredentialBadges}
/>
              </>}>
<PlannerAlerts
  noShowAlerts={noShowAlerts}
  lateShiftAlerts={lateShiftAlerts}
  autoNoShowCandidates={autoNoShowCandidates}
  autoNoShowEnabled={autoNoShowEnabled}
  setAutoNoShowEnabled={setAutoNoShowEnabled}
/>

<TimeRequiredStrip />
<WaitingStrip />

    <PlannerProvider
      value={{
        selectedShiftId,
        selectedShiftIds,
        setSelectedShiftId,
        toggleShiftSelection,
        workersByUid,
        shortWorkerLabel,
        now,
      }}
    >
      {dateScope === "history" ? (
        <PlannerHistory shifts={shiftsActive} now={now} selectedShiftId={selectedShiftId} selectedShiftIds={selectedShiftIds} toggleShiftSelection={toggleShiftSelection} onSelect={setSelectedShiftId} workersByUid={workersByUid} />
      ) : isMobile ? (
        <MobileBoard
          RoleVisibilityStrip={RoleVisibilityStrip}
          SelectionActionBar={SelectionActionBar}
          mobileBoardRole={mobileBoardRole}
          visibleRoleKeys={visibleRoleKeys}
          roleOptions={roleOptions}
          setMobileBoardRole={setMobileBoardRole}
          selectedDayMs={selectedDayMs}
          cardStyle={cardStyle}
          BOARD_BLOCKS={BOARD_BLOCKS}
          dailyByBlockRole={dailyByBlockRole}
          openOverflow={openOverflow}
          PlannerShiftCard={PlannerShiftCard}
          now={now}
          selectedShiftId={selectedShiftId}
          selectedShiftIds={selectedShiftIds}
          toggleShiftSelection={toggleShiftSelection}
          setSelectedShiftId={setSelectedShiftId}
          workersByUid={workersByUid}
          shortWorkerLabel={shortWorkerLabel}
          LifecycleBoxes={LifecycleBoxes}
        />
      ) : (
        <DesktopBoard
          selectedDayMs={selectedDayMs}
          visibleRoleKeys={visibleRoleKeys}
          roleOptions={roleOptions}
          isTablet={isTablet}
          dailyByBlockRole={dailyByBlockRole}
          openOverflow={openOverflow}
          cellHeightForBlock={cellHeightForBlock}
          visibleCardsForCount={visibleCardsForCount}
          cardHeightForCount={cardHeightForCount}
          RoleVisibilityStrip={RoleVisibilityStrip}
          SelectionActionBar={SelectionActionBar}
          PlannerShiftCard={PlannerShiftCard}
          LifecycleBoxes={LifecycleBoxes}
          BOARD_BLOCKS={BOARD_BLOCKS}
          cardStyle={cardStyle}
          selectedShiftId={selectedShiftId}
          selectedShiftIds={selectedShiftIds}
          toggleShiftSelection={toggleShiftSelection}
          setSelectedShiftId={setSelectedShiftId}
          workersByUid={workersByUid}
        />
      )}
    </PlannerProvider>


{dateScope !== "history" && <><CompletedTodayPanel /><PastShiftsPanel /></>}
            </ScheduleWorkspace>
          </>
        ) : mode === "import" ? (
          <div style={cardStyle()}>
            <div style={{ padding: 18 }}>Import schedule UI coming next.</div>
          </div>
        ) : (
          <div style={cardStyle()}>
            <div style={{ padding: 18 }}>API integration coming next.</div>
          </div>
        )}
      </div>}
      </div>
      <FacilityRatingModal
  open={!!ratingShift}
  worker={ratingWorker}
  onClose={() => {
    setRatingShift(null);
    setRatingWorker(null);
  }}
  onSave={async (rating) => {
    await saveFacilityRating({
      shiftId: ratingShift.id,
      workerUid:
        ratingWorker?.uid ||
        ratingWorker?.id ||
        ratingShift.assignedWorkerUid,
      facilityId: ratingShift.facilityId,
      plannerUid: auth.currentUser?.uid || "planner",
      ...rating,
    });

    setRatingShift(null);
    setRatingWorker(null);

    alert("Rating saved.");
  }}
/>
    </DepartmentLayout>
);
}
  
